import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getAllQuestions,
  submitVote,
  getCommentsForQuestion,
  getRepliesForComment,
  addComment
} from '../services/questionsService';
import CommentSection from '../components/CommentSection';
import CommentLeaderboard from '../components/CommentLeaderboard';
import LoginModal from '../components/LoginModal';
import UserMenu from '../components/UserMenu';
import Categories from '../components/Categories';
import AdContainer from '../components/AdContainer';
import AppLogo from '../components/AppLogo';
import TomatoRefillModal from '../components/TomatoRefillModal';
import ClapRefillModal from '../components/ClapRefillModal';
import QuestionImage from '../components/QuestionImage';
import QuestionMedia from '../components/QuestionMedia';
import TomatoCounter from '../components/TomatoCounter';
import ClapCounter from '../components/ClapCounter';
import adConfig from '../services/adConfig';
import { getTomatoCount, getTomatoCountSync, setTomatoCount as setTomatoCountManager, throwTomato, refundTomato, resetTomatoCount } from '../utils/tomatoManagerV2';
import { getClapCountSync, setClapCount as setClapCountManager, useClap, refundClap, resetClapCount } from '../utils/clapManagerV2';
import imageCache from '../utils/imageCache';
import { trackInteraction, getRecommendedQuestions } from '../services/recommendationService';

// TEMP DEBUG - Force category indicator to show
console.log("LOADED VERSION WITH FORCED CATEGORY INDICATOR");

// Calculate hot algorithm based on real engagement metrics
const calculateHotAlgorithm = (question) => {
  const now = new Date();
  const createdAt = new Date(question.createdAt);
  const ageInHours = (now - createdAt) / (1000 * 60 * 60);
  const ageInDays = ageInHours / 24;

  // Get comment count from localStorage
  let commentCount = 0;
  try {
    const localComments = JSON.parse(localStorage.getItem('comments') || '{}');
    commentCount = localComments[question.id]?.length || 0;
  } catch (err) {
    commentCount = 0;
  }

  const totalVotes = question.totalVotes || 0;
  const totalEngagement = totalVotes + commentCount;
  
  // Calculate vote velocity (votes + comments per day)
  const velocity = ageInDays > 0 ? totalEngagement / Math.max(ageInDays, 0.1) : totalEngagement;
  
  // Debug logging
  console.log(`Question ${question.id} metrics:`, {
    ageInHours: ageInHours.toFixed(1),
    ageInDays: ageInDays.toFixed(1),
    totalVotes,
    commentCount,
    totalEngagement,
    velocity: velocity.toFixed(2)
  });

  // NEW: Recently created (< 24 hours)
  if (ageInHours < 24) {
    return 'new';
  }
  
  // TRENDING: High recent activity (high velocity, not too old)
  if (velocity > 5 && ageInDays < 7) {
    return 'trending';
  }
  
  // RISING: Good velocity for newer content (growing momentum)
  if (velocity > 2 && ageInDays < 3) {
    return 'rising';
  }
  
  // POPULAR: High total engagement regardless of age
  if (totalEngagement > 10) {
    return 'popular';
  }

  // Default to trending for questions with some engagement
  if (totalEngagement > 0) {
    return 'trending';
  }

  // Fallback for questions with no engagement - show as new if recent, trending otherwise
  return ageInDays < 1 ? 'new' : 'trending';
};

// Hot Tab component for the popup tabs
const HotTab = ({ name, emoji, color, active, onClick }) => {
  return (
    <div
      style={{
        padding: "6px 12px",
        backgroundColor: active ? color : "rgba(0,0,0,0.4)",
        color: "white",
        borderRadius: "20px",
        display: "flex",
        alignItems: "center",
        gap: "4px",
        fontSize: "14px",
        fontWeight: active ? "600" : "500",
        cursor: "pointer",
        whiteSpace: "nowrap",
        boxShadow: active ? "0 2px 8px rgba(0,0,0,0.3)" : "none",
        border: "1px solid " + (active ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.2)"),
        transition: "all 0.2s ease"
      }}
      onClick={() => {
        // Call the parent onClick handler if provided
        if (onClick) {
          onClick(name.toLowerCase());
        }
      }}
    >
      <span>{emoji}</span>
      <span>{name}</span>
    </div>
  );
};

function Home() {
  const { currentUser } = useAuth();
  const { questionId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [hasMoreQuestions, setHasMoreQuestions] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('forYou'); // 'categories', 'forYou', or 'hot'
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentHotMode, setCurrentHotMode] = useState('trending'); // Default hot mode
  
  // Ad-related states
  const [swipeCounter, setSwipeCounter] = useState(0);
  const [showAd, setShowAd] = useState(false);
  const [adType, setAdType] = useState('display'); // 'display' or 'video'
  const [adFormat, setAdFormat] = useState('fullscreen'); // 'fullscreen', 'banner', or 'interstitial'
  const [isAdFree, setIsAdFree] = useState(false); // Track if user is in ad-free period
  const [adFreeCooldownActive, setAdFreeCooldownActive] = useState(false); // Track if ad-free feature is in cooldown

  // Shop modal state
  const [showShopModal, setShowShopModal] = useState(false);

  // Tomato throw and clap systems
  const [tomatoCount, setTomatoCount] = useState(10);
  const [tomatoRefillsUsedToday, setTomatoRefillsUsedToday] = useState(0);
  const [clapCount, setClapCount] = useState(10);
  const [clapRefillsUsedToday, setClapRefillsUsedToday] = useState(0);
  const [showTomatoRefillModal, setShowTomatoRefillModal] = useState(false);
  const [showClapRefillModal, setShowClapRefillModal] = useState(false);

  // Function to toggle hot topic interests (starring)
  const toggleHotTopicInterest = (hotTopic) => {
    const userKey = currentUser ? currentUser.uid : 'anonymous';
    setHotTopicInterests(prev => {
      const newValue = !prev[hotTopic];
      const newInterests = {
        ...prev,
        [hotTopic]: newValue
      };
      
      // Save to localStorage
      localStorage.setItem(`hotTopicInterests_${userKey}`, JSON.stringify(newInterests));
      
      console.log(`${newValue ? 'Starred' : 'Unstarred'} hot topic: ${hotTopic}`);
      return newInterests;
    });
  };

  // Debug: Log tab changes
  useEffect(() => {
    console.log("Active tab changed to:", activeTab);
  }, [activeTab]);

  // Check for existing ad-free period and cooldown on component mount
  useEffect(() => {
    const userId = currentUser?.uid || 'anonymous_user';
    const adFreeKey = `adFree_${userId}`;
    const adFreeCooldownKey = `adFreeCooldown_${userId}`;
    const adFreeExpiry = localStorage.getItem(adFreeKey);
    const adFreeCooldown = localStorage.getItem(adFreeCooldownKey);

    const now = Date.now();

    // Check if ad-free is active
    if (adFreeExpiry) {
      const expiryTime = parseInt(adFreeExpiry, 10);

      if (expiryTime > now) {
        // User is still in ad-free period
        setIsAdFree(true);

        // Schedule expiration
        const timeRemaining = expiryTime - now;
        console.log(`Ad-free time remaining: ${Math.round(timeRemaining / 1000 / 60)} minutes`);

        setTimeout(() => {
          setIsAdFree(false);
          console.log("Ad-free period expired");
        }, timeRemaining);
      } else {
        // Ad-free period has expired, clear it
        localStorage.removeItem(adFreeKey);
      }
    }

    // Check for cooldown period (even if ad-free is not active)
    if (adFreeCooldown) {
      const cooldownExpiry = parseInt(adFreeCooldown, 10);

      if (cooldownExpiry > now) {
        // User is still in cooldown period - can't activate ad-free again yet
        const cooldownRemaining = cooldownExpiry - now;
        console.log(`Ad-free cooldown remaining: ${Math.round(cooldownRemaining / 1000 / 60)} minutes`);
      } else {
        // Cooldown period has expired, clear it
        localStorage.removeItem(adFreeCooldownKey);
      }
    }
  }, [currentUser]);

  // Initialize tomato and clap systems
  useEffect(() => {
    const loadItemState = () => {
      // Handle storage for both logged-in and anonymous users
      const userId = currentUser?.uid || 'anonymous_user';

      // Tomato system keys - using tomatoManager for tomato counts
      const tomatoRefillsKey = `tomatoRefills_${userId}`;
      const lastTomatoRefillKey = `lastTomatoRefill_${userId}`;

      // Clap system keys
      const clapKey = `claps_${userId}`;
      const clapRefillsKey = `clapRefills_${userId}`;
      const lastClapRefillKey = `lastClapRefill_${userId}`;

      try {
        // We'll handle resetting the tomato count in the daily refill logic below
        // This ensures we don't reset twice
        
        // Get the current tomato count
        const tomatoCount = getTomatoCountSync(userId);

        // Get stored tomato refills used today or default to 0
        const storedTomatoRefills = localStorage.getItem(tomatoRefillsKey);
        const tomatoRefills = storedTomatoRefills ? parseInt(storedTomatoRefills, 10) : 0;

        // Get stored clap count or default to 10
        const storedClapCount = localStorage.getItem(clapKey);
        const clapCount = storedClapCount ? parseInt(storedClapCount, 10) : 10;

        // Get stored clap refills used today or default to 0
        const storedClapRefills = localStorage.getItem(clapRefillsKey);
        const clapRefills = storedClapRefills ? parseInt(storedClapRefills, 10) : 0;

        // Get last refill timestamps
        const lastTomatoRefillTimestamp = localStorage.getItem(lastTomatoRefillKey);
        const lastClapRefillTimestamp = localStorage.getItem(lastClapRefillKey);
        const now = new Date();

        // Process tomato refills
        if (lastTomatoRefillTimestamp) {
          const lastTomatoRefill = new Date(parseInt(lastTomatoRefillTimestamp, 10));
          const hoursSinceLastTomatoRefill = (now - lastTomatoRefill) / (1000 * 60 * 60);

          // If it's been more than 24 hours, reset tomato counts
          if (hoursSinceLastTomatoRefill >= 24) {
            // Reset to 10 tomatoes and 0 refills used using tomatoManager
            resetTomatoCount(userId);
            setTomatoRefillsUsedToday(0);

            // Update remaining storage values (refills count and timestamp)
            localStorage.setItem(tomatoRefillsKey, '0');
            localStorage.setItem(lastTomatoRefillKey, now.getTime().toString());

            console.log('Daily tomato refill completed');
          } else {
            // Just set the state values from storage
            setTomatoCount(tomatoCount);
            setTomatoRefillsUsedToday(tomatoRefills);
            console.log('Loaded tomato state:', { tomatoCount, tomatoRefills });
          }
        } else {
          // First time setup for tomatoes - set the last refill time
          localStorage.setItem(lastTomatoRefillKey, now.getTime().toString());
          setTomatoCount(tomatoCount);
          setTomatoRefillsUsedToday(tomatoRefills);
        }

        // Process clap refills
        if (lastClapRefillTimestamp) {
          const lastClapRefill = new Date(parseInt(lastClapRefillTimestamp, 10));
          const hoursSinceLastClapRefill = (now - lastClapRefill) / (1000 * 60 * 60);

          // If it's been more than 24 hours, reset clap counts
          if (hoursSinceLastClapRefill >= 24) {
            // Reset to 10 claps and 0 refills used
            setClapCount(10);
            setClapRefillsUsedToday(0);

            // Update storage with new values
            localStorage.setItem(clapKey, '10');
            localStorage.setItem(clapRefillsKey, '0');
            localStorage.setItem(lastClapRefillKey, now.getTime().toString());

            console.log('Daily clap refill completed');
          } else {
            // Just set the state values from storage
            setClapCount(clapCount);
            setClapRefillsUsedToday(clapRefills);
            console.log('Loaded clap state:', { clapCount, clapRefills });
          }
        } else {
          // First time setup for claps - set the last refill time
          localStorage.setItem(lastClapRefillKey, now.getTime().toString());
          setClapCount(clapCount);
          setClapRefillsUsedToday(clapRefills);
        }
      } catch (error) {
        console.error('Error loading item state:', error);
        // Use defaults if there's an error
        setTomatoCount(10);
        setTomatoRefillsUsedToday(0);
        setClapCount(10);
        setClapRefillsUsedToday(0);
      }
    };

    loadItemState();

    // Set up event listeners for tomato and clap systems
    const handleCheckTomatoAvailability = (event) => {
      const callback = event.detail?.callback;
      if (callback && typeof callback === 'function') {
        const userId = currentUser?.uid || 'anonymous_user';
        const hasTomatoesAvailable = getTomatoCountSync(userId) > 0;
        if (hasTomatoesAvailable) {
          callback(true);
        } else {
          // Show refill modal if no tomatoes
          setShowTomatoRefillModal(true);
          callback(false);
        }
      }
    };

    const handleUseTomato = (event) => {
      // Extract the user ID from the event details if available, otherwise use the current user
      const eventUserId = event?.detail?.userId;
      const userId = eventUserId || currentUser?.uid || 'anonymous_user';
      
      // Check if we have a direct count provided from the component
      const directCount = event?.detail?.directCount;
      if (directCount !== undefined) {
        // Use the direct count sent by the component
        console.log(`Setting tomato count directly to ${directCount} from component`);
        
        // Use tomatoManager to set the count
        setTomatoCountManager(userId, directCount);
        
        // Update React state to match
        setTomatoCount(directCount);
        return;
      }
      
      // If no direct count, use the throwTomato utility to decrease count by 1
      const newCount = throwTomato(userId);
      
      // Update React state to match
      setTomatoCount(newCount);
      
      // If this was the last tomato, prompt for refill
      if (newCount === 0) {
        setTimeout(() => {
          setShowTomatoRefillModal(true);
        }, 300);
      }
      
      console.log(`Tomato count reduced to ${newCount} for user ${userId}`);
    };

    const handleRefundTomato = (event) => {
      // Extract the user ID from the event details if available, otherwise use the current user
      const eventUserId = event?.detail?.userId;
      const userId = eventUserId || currentUser?.uid || 'anonymous_user';
      
      // Check if we have a direct count provided from the component
      const directCount = event?.detail?.directCount;
      if (directCount !== undefined) {
        // Use the direct count sent by the component
        console.log(`Setting tomato count directly to ${directCount} from component`);
        
        // Use tomatoManager to set the count
        setTomatoCountManager(userId, directCount);
        
        // Update React state to match
        setTomatoCount(directCount);
        return;
      }
      
      // Refund one tomato using tomatoManager
      const newCount = refundTomato(userId);
      
      // Update React state to match
      setTomatoCount(newCount);
      
      console.log(`Tomato count increased to ${newCount} for user ${userId}`);
    };

    // Clap handlers
    const handleCheckClapAvailability = (event) => {
      const callback = event.detail?.callback;
      if (callback && typeof callback === 'function') {
        const hasClapsAvailable = clapCount > 0;
        if (hasClapsAvailable) {
          callback(true);
        } else {
          // Show refill modal for claps
          setShowTomatoRefillModal(true); // Reuse same modal
          callback(false);
        }
      }
    };

    const handleUseClap = () => {
      // Use one clap - decrease count by 1
      setClapCount(prev => {
        const newCount = Math.max(0, prev - 1);
        // Store for logged-in or anonymous
        const userId = currentUser?.uid || 'anonymous_user';
        const clapKey = `claps_${userId}`;
        localStorage.setItem(clapKey, newCount.toString());
        return newCount;
      });

      // If this was the last clap, prompt for refill
      if (clapCount === 1) {
        setTimeout(() => {
          setShowTomatoRefillModal(true); // Reuse same modal
        }, 300);
      }
    };

    const handleRefundClap = () => {
      // Refund one clap
      setClapCount(prev => {
        const newCount = prev + 1;
        // Store for logged-in or anonymous
        const userId = currentUser?.uid || 'anonymous_user';
        const clapKey = `claps_${userId}`;
        localStorage.setItem(clapKey, newCount.toString());
        return newCount;
      });
    };

    // Add event listeners for tomatoes and claps
    window.addEventListener('checkTomatoAvailability', handleCheckTomatoAvailability);
    window.addEventListener('useTomato', handleUseTomato);
    window.addEventListener('refundTomato', handleRefundTomato);
    window.addEventListener('checkClapAvailability', handleCheckClapAvailability);
    window.addEventListener('useClap', handleUseClap);
    window.addEventListener('refundClap', handleRefundClap);

    // Clean up event listeners
    return () => {
      window.removeEventListener('checkTomatoAvailability', handleCheckTomatoAvailability);
      window.removeEventListener('useTomato', handleUseTomato);
      window.removeEventListener('refundTomato', handleRefundTomato);
      window.removeEventListener('checkClapAvailability', handleCheckClapAvailability);
      window.removeEventListener('useClap', handleUseClap);
      window.removeEventListener('refundClap', handleRefundClap);
    };
  }, [currentUser, tomatoCount, clapCount]);

  // Monitor for tomatoManager count changes using its event system
  // This creates a centralized way for all components to stay in sync with the count
  useEffect(() => {
    // This handler receives events from tomatoManager when counts change
    // from any component in the application
    const handleTomatoCountChanged = (e) => {
      const { userId, count } = e.detail;
      const currentUserId = currentUser?.uid || 'anonymous_user';
      
      // Only update our state if it's for the current user
      if (userId === currentUserId) {
        console.log(`Tomato count changed event received: ${count} for user ${userId}`);
        setTomatoCount(count);
      }
    };
    
    // Initial sync with tomatoManager
    const userId = currentUser?.uid || 'anonymous_user';
    const currentCount = getTomatoCountSync(userId);
    setTomatoCount(currentCount);
    
    // Listen for tomato count changes from the manager
    document.addEventListener('tomatoCountChanged', handleTomatoCountChanged);
    
    // Clean up on unmount
    return () => {
      document.removeEventListener('tomatoCountChanged', handleTomatoCountChanged);
    };
  }, [currentUser]);
  
  // Sync React state with tomatoManager when state changes
  useEffect(() => {
    // Save for both logged-in and anonymous users
    const userId = currentUser?.uid || 'anonymous_user';
    
    // Use tomatoManager to set the count (it will handle localStorage updates)
    setTomatoCountManager(userId, tomatoCount);
    console.log(`Synced tomato count with manager: ${tomatoCount} for user: ${userId}`);
  }, [tomatoCount, currentUser]);

  // Save tomato refills used whenever it changes
  useEffect(() => {
    // Save for both logged-in and anonymous users
    const userId = currentUser?.uid || 'anonymous_user';
    const refillsKey = `tomatoRefills_${userId}`;

    localStorage.setItem(refillsKey, tomatoRefillsUsedToday.toString());
    console.log(`Saved tomato refills used: ${tomatoRefillsUsedToday} for user: ${userId}`);
  }, [tomatoRefillsUsedToday, currentUser]);

  // Save clap count whenever it changes
  useEffect(() => {
    // Save for both logged-in and anonymous users
    const userId = currentUser?.uid || 'anonymous_user';
    const clapKey = `claps_${userId}`;

    localStorage.setItem(clapKey, clapCount.toString());
    console.log(`Saved clap count: ${clapCount} for user: ${userId}`);
  }, [clapCount, currentUser]);

  // Save clap refills used whenever it changes
  useEffect(() => {
    // Save for both logged-in and anonymous users
    const userId = currentUser?.uid || 'anonymous_user';
    const refillsKey = `clapRefills_${userId}`;

    localStorage.setItem(refillsKey, clapRefillsUsedToday.toString());
    console.log(`Saved clap refills used: ${clapRefillsUsedToday} for user: ${userId}`);
  }, [clapRefillsUsedToday, currentUser]);

  // Function to use a tomato
  const useTomato = () => {
    // Check for tomatoes using functional update to ensure latest value
    let hasEnoughTomatoes = false;
    
    setTomatoCount(prev => {
      if (prev <= 0) {
        // Show refill modal if user is out of tomatoes
        setShowTomatoRefillModal(true);
        return prev; // Don't change the count
      }
      
      // We have enough tomatoes
      hasEnoughTomatoes = true;
      
      // Calculate new count
      const newCount = prev - 1;
      
      // If this was the last tomato, prompt for refill
      if (newCount === 0) {
        setTimeout(() => {
          setShowTomatoRefillModal(true);
        }, 300);
      }
      
      // Return the decremented count
      return newCount;
    });
    
    return hasEnoughTomatoes;
  };

  // Function to refill tomatoes by watching an ad
  const refillTomatoesByAd = () => {
    if (tomatoRefillsUsedToday >= 2) {
      alert("You've used your maximum ad refills for today. Wait for tomorrow's refill or purchase more.");
      return;
    }

    // Show an ad
    setAdType('video');
    setAdFormat('fullscreen');
    setShowAd(true);

    // When ad finishes, refill tomatoes
    // This is handled in the ad dismiss callback
    const refillTomatoes = () => {
      setTomatoCount(10);
      setTomatoRefillsUsedToday(prev => prev + 1);
      setShowTomatoRefillModal(false);

      // Save the new tomato count to localStorage immediately
      const userId = currentUser?.uid || 'anonymous_user';
      const tomatoKey = `tomatoes_${userId}`;
      const refillsKey = `tomatoRefills_${userId}`;

      localStorage.setItem(tomatoKey, '10');
      localStorage.setItem(refillsKey, tomatoRefillsUsedToday.toString());

      alert("Thanks for watching! 10 tomatoes have been added to your account.");
    };

    // Set up a one-time event handler for ad close
    const handleAdClose = () => {
      refillTomatoes();
      // Remove the event listener
      window.removeEventListener('adClosed', handleAdClose);
    };

    // Add the event listener
    window.addEventListener('adClosed', handleAdClose);
  };

  // Function to purchase tomato pack
  const purchaseTomatoPack = (amount) => {
    // In a real app, this would handle payment processing
    console.log(`Purchasing tomato pack: ${amount}`);

    // For demo purposes, just add the tomatoes
    setTomatoCount(prev => {
      const newCount = prev + amount;

      // Save the new tomato count to localStorage immediately
      const userId = currentUser?.uid || 'anonymous_user';
      const tomatoKey = `tomatoes_${userId}`;
      localStorage.setItem(tomatoKey, newCount.toString());

      return newCount;
    });

    setShowTomatoRefillModal(false);
    setShowShopModal(false);

    alert(`${amount} tomatoes have been added to your account!`);
  };

  // Function to watch an ad for 20 minutes ad-free browsing
  const watchAdForAdFree = () => {
    // Check if user is already in a cooldown period
    const userId = currentUser?.uid || 'anonymous_user';
    const adFreeCooldownKey = `adFreeCooldown_${userId}`;
    const cooldownExpiry = localStorage.getItem(adFreeCooldownKey);

    if (cooldownExpiry) {
      const expiryTime = parseInt(cooldownExpiry, 10);
      const now = Date.now();

      if (expiryTime > now) {
        // Still in cooldown period, cannot use this feature yet
        const minutesRemaining = Math.ceil((expiryTime - now) / (60 * 1000));
        alert(`This feature is on cooldown. Please try again in ${minutesRemaining} minutes.`);
        return;
      }
    }

    // Show an ad
    setAdType('video');
    setAdFormat('fullscreen');
    setShowAd(true);

    // When ad finishes, enable ad-free period
    const enableAdFree = () => {
      setIsAdFree(true);
      setAdFreeCooldownActive(true);
      setShowShopModal(false);

      const now = Date.now();
      const adFreeDuration = 20 * 60 * 1000; // 20 minutes in milliseconds

      // Save ad-free status in localStorage
      const adFreeKey = `adFree_${userId}`;
      const adFreeExpiry = now + adFreeDuration;
      localStorage.setItem(adFreeKey, adFreeExpiry.toString());

      // Also set the cooldown period (same duration as ad-free)
      localStorage.setItem(adFreeCooldownKey, adFreeExpiry.toString());

      alert("Thanks for watching! You'll have 20 minutes of ad-free browsing. This feature will be unavailable for 20 minutes after it expires.");

      // Schedule the removal of ad-free status after 20 minutes
      setTimeout(() => {
        setIsAdFree(false);
        console.log("Ad-free period expired");
      }, adFreeDuration);

      // Schedule the end of cooldown (same time as ad-free expiry)
      setTimeout(() => {
        setAdFreeCooldownActive(false);
        console.log("Ad-free cooldown ended");
      }, adFreeDuration);
    };

    // Set up a one-time event handler for ad close
    const handleAdClose = () => {
      enableAdFree();
      // Remove the event listener
      window.removeEventListener('adClosed', handleAdClose);
    };

    // Add the event listener
    window.addEventListener('adClosed', handleAdClose);
  };

  // Handle hot tab click
  const handleHotTabClick = async (hotMode) => {
    console.log(`Switching to hot mode: ${hotMode}`);
    setCurrentHotMode(hotMode);
    setLoading(true);

    try {
      // Track user preference for this hot algorithm
      if (currentUser) {
        const hotTabPrefsKey = `hotTabPreferences_${currentUser.uid}`;
        const hotTabPrefs = JSON.parse(localStorage.getItem(hotTabPrefsKey) || '{}');
        hotTabPrefs[hotMode] = (hotTabPrefs[hotMode] || 0) + 1;
        localStorage.setItem(hotTabPrefsKey, JSON.stringify(hotTabPrefs));
      }

      // Fetch questions using this hot algorithm
      const userId = currentUser ? currentUser.uid : 'anonymous_user';
      const questionsData = await getAllQuestions(1, 10, userId, false, hotMode);

      if (questionsData.questions && questionsData.questions.length > 0) {
        // Immediately start loading the first image
        if (questionsData.questions[0]?.imageUrl) {
          const firstImg = new Image();
          firstImg.src = questionsData.questions[0].imageUrl;
        }
        
        setQuestions(questionsData.questions);
        setHasMoreQuestions(questionsData.hasMore);
        setCurrentQuestionIndex(0);
        setPage(1);

        // Preload next 4 images (first one already started)
        const imagesToPreload = questionsData.questions
          .slice(1, 5)
          .map(q => q.imageUrl)
          .filter(Boolean);
        imageCache.preloadImages(imagesToPreload);

        // Check if user has voted on first question
        if (questionsData.questions.length > 0) {
          const firstQuestionId = questionsData.questions[0].id;
          const voted = checkUserVotedOnQuestion(firstQuestionId);
          setHasVoted(voted);
        }
      }
    } catch (error) {
      console.error(`Error fetching questions for hot mode ${hotMode}:`, error);
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to check user votes consistently across the app
  const checkUserVotedOnQuestion = (questionID) => {
    if (!questionID) return false;

    if (currentUser) {
      // User-specific storage key for logged-in users
      const userVotedQuestionsKey = `votedQuestions_${currentUser.uid}`;
      const votedQuestions = JSON.parse(localStorage.getItem(userVotedQuestionsKey) || '{}');
      const hasVoted = votedQuestions[questionID] !== undefined;
      console.log(`User ${currentUser.uid} vote check for question ${questionID}: ${hasVoted}`);
      return hasVoted;
    } else {
      // Anonymous-only storage - completely separate from logged-in users
      const anonVotedQuestionsKey = `votedQuestions_anonymous`;
      const votedQuestions = JSON.parse(localStorage.getItem(anonVotedQuestionsKey) || '{}');
      const hasVoted = votedQuestions[questionID] !== undefined;
      console.log(`Anonymous user vote check for question ${questionID}: ${hasVoted}`);
      return hasVoted;
    }
  };

  // Helper function to get the actual vote option for a question
  const getUserVoteOption = (questionID) => {
    if (!questionID || !currentUser) return null;

    // User-specific storage key for logged-in users
    const userVotedQuestionsKey = `votedQuestions_${currentUser.uid}`;
    const votedQuestions = JSON.parse(localStorage.getItem(userVotedQuestionsKey) || '{}');

    const voteData = votedQuestions[questionID];
    if (!voteData) return null;

    // Handle both old and new formats
    if (typeof voteData === 'object' && voteData !== null) {
      return voteData.option;
    } else {
      return voteData; // Old format - just the option value
    }
  };
  
  // Animation states
  const [animatingResults, setAnimatingResults] = useState(false);
  const [displayPercentA, setDisplayPercentA] = useState(0);
  const [displayPercentB, setDisplayPercentB] = useState(0);
  const [userChoice, setUserChoice] = useState(null);
  const [animationStep, setAnimationStep] = useState(0); // 0: not started, 1: tallying, 2: complete
  
  // Refs for swipe detection
  const questionCardRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const autoAdvanceTimerRef = useRef(null);
  const isScrollingRef = useRef(false);
  const animationTimerRef = useRef(null);
  
  // Current active question
  const question = questions[currentQuestionIndex] || null;
  
  // DEBUG: Log current state every render
  console.log(`🔍 RENDER STATE: Index=${currentQuestionIndex}, Total=${questions.length}, Question="${question?.questionText?.slice(0, 30)}..."`);
  if (questions.length > 0) {
    console.log('🔍 ALL QUESTIONS:', questions.map((q, i) => `${i}: ${q.questionText.slice(0, 20)}...`));
  }

  // States for category indicator
  const [showCategoryIndicator, setShowCategoryIndicator] = useState(false);
  const [categoryInfo, setCategoryInfo] = useState(null);
  
  // State for hot topic interests (starred hot topics)
  const [hotTopicInterests, setHotTopicInterests] = useState(() => {
    try {
      const userKey = currentUser ? currentUser.uid : 'anonymous';
      const saved = localStorage.getItem(`hotTopicInterests_${userKey}`);
      return saved ? JSON.parse(saved) : {};
    } catch (err) {
      return {};
    }
  });
  
  // State to track initial load to prevent flicker animations
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Ad-related countdown state
  const [adCountdown, setAdCountdown] = useState(15);
  const [canSkipAd, setCanSkipAd] = useState(false);
  const adCountdownTimerRef = useRef(null);

  // Track time spent on questions and view interactions
  useEffect(() => {
    if (!question || !currentUser) return;

    const startTime = Date.now();
    const questionId = question.id;
    const userId = currentUser.uid;
    
    // Track view interaction immediately
    trackInteraction(userId, questionId, 'view', question.category);

    // Track when user starts viewing a question
    return () => {
      // When component unmounts or question changes, calculate time spent
      const timeSpent = Math.floor((Date.now() - startTime) / 1000); // in seconds

      // Only record if user spent at least 2 seconds on the question
      if (timeSpent >= 2) {
        try {
          // Store time spent in localStorage
          const timeSpentKey = `questionTimeSpent_${userId}`;
          const existingData = JSON.parse(localStorage.getItem(timeSpentKey) || '{}');

          // Update with new time (add to existing if user viewed multiple times)
          existingData[questionId] = (existingData[questionId] || 0) + timeSpent;

          localStorage.setItem(timeSpentKey, JSON.stringify(existingData));
          console.log(`Tracked ${timeSpent}s spent on question ${questionId}`);
        } catch (err) {
          console.warn('Error storing time spent data:', err);
        }
      }
    };
  }, [question, currentUser]);

  // Track time-of-day preferences
  useEffect(() => {
    if (!question || !question.category || !currentUser) return;

    const categoryId = question.category;
    const userId = currentUser.uid;
    const currentHour = new Date().getHours();

    const timeOfDay =
      currentHour >= 5 && currentHour < 12 ? 'morning' :
      currentHour >= 12 && currentHour < 17 ? 'afternoon' :
      currentHour >= 17 && currentHour < 22 ? 'evening' : 'night';

    try {
      // Try to update the time-of-day preferences
      const timePrefsKey = `timePreferences_${userId}`;
      const savedTimePrefs = JSON.parse(localStorage.getItem(timePrefsKey) || '{}');

      // Initialize if not present
      if (!savedTimePrefs[timeOfDay]) {
        savedTimePrefs[timeOfDay] = [];
      }

      // Add the category to this time of day if not already present
      if (!savedTimePrefs[timeOfDay].includes(categoryId)) {
        savedTimePrefs[timeOfDay].push(categoryId);
        localStorage.setItem(timePrefsKey, JSON.stringify(savedTimePrefs));
      }
    } catch (err) {
      console.warn('Error updating time-of-day preferences:', err);
    }
  }, [question, currentUser]);

  // Show category indicator when question changes - FORCED ALWAYS ON VERSION
  useEffect(() => {
    console.log("Effect running - FORCE SHOWING CATEGORY", question);

    if (!question) {
      console.log("No question available yet");
      return;
    }

    // FORCE a category even if none exists
    const categoryId = question.category || "1";
    console.log("Using category ID:", categoryId);

    // Get category info for the indicator
    const categoryMapping = {
      // Standard categories - match Categories.js
      "1": { name: 'Life', color: '#FF6B00', emoji: '⚛️' },
      "2": { name: 'Food', color: '#E67E22', emoji: '🍔' },
      "3": { name: 'Dating', color: '#FFC107', emoji: '❤️' },
      "4": { name: 'Fashion', color: '#E91E63', emoji: '👗' },
      "5": { name: 'Politics', color: '#2196F3', emoji: '🗳️' },
      "6": { name: 'Sports', color: '#4CAF50', emoji: '⚽' },
      "7": { name: 'Technology', color: '#9C27B0', emoji: '📱' },
      "8": { name: 'Movies', color: '#FF5722', emoji: '🎬' },
      "9": { name: 'Music', color: '#607D8B', emoji: '🎵' },
      "10": { name: 'Out of the Box', color: '#3F51B5', emoji: '💭' }
    };

    // Find category info or use default
    const category = categoryMapping[categoryId] || {
      name: 'Category ' + (categoryId || 'Unknown'),
      color: '#FF5722',
      emoji: '🔥'
    };

    console.log("Setting category info:", category);

    // Set category info
    setCategoryInfo(category);

    // ALWAYS show the indicator
    setShowCategoryIndicator(true);
    console.log("Indicator state set to TRUE");

    // Hide the indicator after longer time (5 seconds)
    const timer = setTimeout(() => {
      setShowCategoryIndicator(false);
      console.log("Indicator timer fired - set to FALSE");
    }, 5000);

    // Clean up
    return () => clearTimeout(timer);
  }, [question]);

  // Fetch comment count for the current question
  useEffect(() => {
    const fetchCommentCount = async () => {
      if (question) {
        try {
          const commentsData = await getCommentsForQuestion(question.id, 1, 1);
          setCommentCount(commentsData.totalCount || 0);
        } catch (error) {
          console.error('Error fetching comment count:', error);
        }
      }
    };

    fetchCommentCount();
  }, [question]);
  
  // Listen for openLoginModal event (from CommentSection)
  useEffect(() => {
    const handleOpenLoginModal = () => {
      setLoginModalOpen(true);
    };

    window.addEventListener('openLoginModal', handleOpenLoginModal);

    return () => {
      window.removeEventListener('openLoginModal', handleOpenLoginModal);
    };
  }, []);

  // Listen for openTomatoRefillModal event
  useEffect(() => {
    const handleOpenTomatoRefillModal = () => {
      console.log('handleOpenTomatoRefillModal called - setting modal to true');
      setShowTomatoRefillModal(true);
    };

    window.addEventListener('openTomatoRefillModal', handleOpenTomatoRefillModal);

    return () => {
      window.removeEventListener('openTomatoRefillModal', handleOpenTomatoRefillModal);
    };
  }, []);

  // Listen for openClapRefillModal event
  useEffect(() => {
    const handleOpenClapRefillModal = () => {
      console.log('handleOpenClapRefillModal called - setting modal to true');
      setShowClapRefillModal(true);
    };

    window.addEventListener('openClapRefillModal', handleOpenClapRefillModal);

    return () => {
      window.removeEventListener('openClapRefillModal', handleOpenClapRefillModal);
    };
  }, []);

  // Listen for category selection events
  useEffect(() => {
    const handleCategorySelection = (event) => {
      console.log('Category selection event received:', event.detail);

      // Only switch tabs if not staying in categories tab
      if (!event.detail.stayInCategoriesTab) {
        // Switch to For You tab for old event type (backward compatibility)
        setActiveTab('forYou');

        // Set selected category
        setSelectedCategory({
          id: event.detail.categoryId,
          name: event.detail.categoryName
        });

        // Update questions with filtered list if provided
        if (event.detail.questions && event.detail.questions.length > 0) {
          setQuestions(event.detail.questions);
          setCurrentQuestionIndex(0);
          setHasMoreQuestions(false);

          // Check if user has already voted on the first question
          if (event.detail.questions.length > 0) {
            const firstQuestionId = event.detail.questions[0].id;
            const voted = checkUserVotedOnQuestion(firstQuestionId);
            setHasVoted(voted);
          }
        }
      }
      // If stayInCategoriesTab is true, Categories.js handles its own UI
    };

    // Listen for both old and new event types
    window.addEventListener('switchToForYouTab', handleCategorySelection);
    window.addEventListener('showCategoryQuestions', handleCategorySelection);

    return () => {
      window.removeEventListener('switchToForYouTab', handleCategorySelection);
      window.removeEventListener('showCategoryQuestions', handleCategorySelection);
    };
  }, []);
  
  // Recheck hasVoted when currentUser changes
  useEffect(() => {
    if (question) {
      const voted = checkUserVotedOnQuestion(question.id);
      console.log('User changed. Rechecking vote status:', { 
        questionId: question.id, 
        hasVoted: voted,
        currentUser: currentUser?.uid || 'anonymous'
      });
      setHasVoted(voted);
    }
  }, [currentUser]);
  
  // Fetch questions
  useEffect(() => {
    console.log('🔄 Home useEffect triggered:', { 
      activeTab, 
      currentUser: currentUser?.email || currentUser?.uid || 'none',
      questionId,
      loading 
    });

    const fetchQuestions = async () => {
      try {
        console.log('🏠 Home component: Fetching questions...');
        console.log('👤 Current user:', currentUser?.email || currentUser?.uid || 'none');

        // If questionId is provided, fetch that specific question first
        if (questionId) {
          try {
            const { getQuestionById } = await import('../services/questionsService');
            const specificQuestion = await getQuestionById(questionId);
            
            if (specificQuestion) {
              console.log('Loading specific question:', questionId);
              setQuestions([specificQuestion]);
              setCurrentQuestionIndex(0);
              setHasMoreQuestions(false);
              setPage(1);
              
              // Check if user has voted on this specific question
              const voted = checkUserVotedOnQuestion(specificQuestion.id);
              setHasVoted(voted);
              
              // Cache this question
              const cachedQuestions = JSON.parse(localStorage.getItem('cachedQuestions') || '{}');
              cachedQuestions[specificQuestion.id] = {
                styles: specificQuestion.styles || {
                  optionABackgroundColor: 'white',
                  optionATextColor: 'black',
                  optionBBackgroundColor: 'black',
                  optionBTextColor: 'white'
                },
                questionType: specificQuestion.questionType || 'standard',
                questionText: specificQuestion.questionText,
                optionA: specificQuestion.optionA,
                optionB: specificQuestion.optionB,
                leaderboardOptions: specificQuestion.leaderboardOptions,
                leaderboardStyles: specificQuestion.leaderboardStyles
              };
              localStorage.setItem('cachedQuestions', JSON.stringify(cachedQuestions));
              localStorage.setItem(`question_text_${specificQuestion.id}`, specificQuestion.questionText || '');
              
              setLoading(false);
              return;
            }
          } catch (error) {
            console.error('Error fetching specific question:', error);
            // If specific question fails, fall back to regular question loading
          }
        }

        // Get userId
        const userId = currentUser ? currentUser.uid : 'anonymous_user';
        let questionsData;

        // Handle different tab types
        if (activeTab === 'forYou') {
          // For You tab - use recommendation algorithm
          console.log("Using recommendation algorithm for For You tab, User ID:", userId);
          
          try {
            // Get all questions first
            const allQuestionsData = await getAllQuestions(1, 50); // Get a reasonable number of questions
            
            if (allQuestionsData.questions && allQuestionsData.questions.length > 0) {
              // Get recommended questions using our algorithm
              const recommendedQuestions = await getRecommendedQuestions(
                currentUser?.uid,
                allQuestionsData.questions,
                selectedCategory
              );
              
              // 🔥 FIXED: Take ALL recommended questions (not just 10)
              console.log(`🎯 RECOMMENDATION FIX: Showing all ${recommendedQuestions.length} questions instead of limiting to 10`);
              questionsData = {
                questions: recommendedQuestions, // Show ALL questions
                hasMore: false // No pagination needed - show everything
              };
              
              // No need to store remaining recommendations since we're showing all
              window._remainingRecommendations = [];
            } else {
              // Fallback to regular algorithm if no questions
              console.log("No questions for recommendations, falling back to regular algorithm");
              questionsData = await getAllQuestions(1, 10, userId, true);
            }
          } catch (error) {
            console.error("Error with recommendation algorithm, falling back:", error);
            // Fallback to regular personalized algorithm
            questionsData = await getAllQuestions(1, 10, userId, true);
          }
        }
        else if (activeTab === 'hot') {
          // Hot tab - initially show trending as default
          console.log("Using 'trending' hot algorithm for Hot tab");
          questionsData = await getAllQuestions(1, 10, userId, false, 'trending');

          // Increment the trending algorithm counter in user preferences
          try {
            if (currentUser) {
              const hotTabPrefsKey = `hotTabPreferences_${userId}`;
              const hotTabPrefs = JSON.parse(localStorage.getItem(hotTabPrefsKey) || '{}');
              hotTabPrefs['trending'] = (hotTabPrefs['trending'] || 0) + 1;
              localStorage.setItem(hotTabPrefsKey, JSON.stringify(hotTabPrefs));
            }
          } catch (err) {
            console.warn('Error updating hot tab preferences:', err);
          }
        }
        else {
          // Categories tab - no personalization
          console.log("Using standard algorithm for Categories tab");
          questionsData = await getAllQuestions(1, 10);
        }

        console.log('Home component: Received questions data:', questionsData);
        console.log('Questions array:', questionsData?.questions);
        console.log('Questions length:', questionsData?.questions?.length);

        if (questionsData && questionsData.questions && questionsData.questions.length > 0) {
          // Mark questions with hot algorithm based on real engagement data
          if (activeTab === 'forYou') {
            questionsData.questions.forEach((question, index) => {
              const hotAlgorithm = calculateHotAlgorithm(question);
              question._hotAlgorithmUsed = hotAlgorithm;
              console.log(`Question ${index} (ID: ${question.id}): assigned hot algorithm: ${hotAlgorithm} based on engagement`);
            });
          }
          
          // Immediately start loading the first image
          if (questionsData.questions[0]?.imageUrl) {
            const firstImg = new Image();
            firstImg.src = questionsData.questions[0].imageUrl;
          }
          
          setQuestions(questionsData.questions);
          setHasMoreQuestions(questionsData.hasMore);
          
          // Reset to first question if needed
          if (currentQuestionIndex >= questionsData.questions.length) {
            setCurrentQuestionIndex(0);
          }

          // Preload next 4 images (first one already started)
          const imagesToPreload = questionsData.questions
            .slice(1, 5)
            .map(q => q.imageUrl)
            .filter(Boolean);
          imageCache.preloadImages(imagesToPreload);

          // Check if user has already voted using our utility function
          if (questionsData.questions.length > 0) {
            const firstQuestionId = questionsData.questions[0].id;
            const voted = checkUserVotedOnQuestion(firstQuestionId);
            console.log('Setting initial hasVoted state:', voted);
            setHasVoted(voted);
          }

          // Store questions in localStorage for access from comments
          const cachedQuestions = JSON.parse(localStorage.getItem('cachedQuestions') || '{}');

          console.log('Caching questions with types:', questionsData.questions.map(q => ({
            id: q.id,
            questionType: q.questionType || 'standard'
          })));

          questionsData.questions.forEach(question => {
            // Store the question text in localStorage for domain-specific filtering
            localStorage.setItem(`question_text_${question.id}`, question.questionText || '');

            cachedQuestions[question.id] = {
              styles: question.styles || {
                optionABackgroundColor: 'white',
                optionATextColor: 'black',
                optionBBackgroundColor: 'black',
                optionBTextColor: 'white'
              },
              // Store question type for reference in other components
              questionType: question.questionType || 'standard',
              // Store other relevant properties
              questionText: question.questionText,
              optionA: question.optionA,
              optionB: question.optionB,
              leaderboardOptions: question.leaderboardOptions,
              leaderboardStyles: question.leaderboardStyles
            };
          });
          localStorage.setItem('cachedQuestions', JSON.stringify(cachedQuestions));

          console.log('Home component: Questions loaded successfully', questionsData.questions);
        } else {
          console.warn('Home component: No questions found in the response');
          setQuestions([]); // Ensure we set an empty array
          setHasMoreQuestions(false);
        }
      } catch (error) {
        console.error('❌ Error fetching questions:', error);
        console.error('Error details:', {
          message: error.message,
          code: error.code,
          stack: error.stack
        });
        setQuestions([]);
        setHasMoreQuestions(false);
      } finally {
        console.log('✅ Setting loading to false in Home component');
        setLoading(false);
      }
    };

    // Prevent unnecessary refetches during rapid auth state changes
    const timeoutId = setTimeout(() => {
      console.log('🚀 Starting fetchQuestions after auth stabilization...');
      fetchQuestions();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [activeTab, currentUser?.uid, questionId]); // Only refetch when userId changes, not full currentUser object
  
  // Loading timeout - force loading to false after 10 seconds to prevent black screen
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        console.warn('⚠️ Loading timeout reached - forcing loading to false to prevent black screen');
        setLoading(false);
        setLoadingTimeout(true);
      }
    }, 10000); // 10 seconds timeout

    return () => clearTimeout(timeout);
  }, [loading]);
  
  // Function to load more questions
  const loadMoreQuestions = async () => {
    if (loadingMore || !hasMoreQuestions) return;

    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const userId = currentUser ? currentUser.uid : 'anonymous_user';
      let moreQuestionsData;

      // Handle different tab types for loading more questions
      if (activeTab === 'forYou') {
        // For You tab - use stored recommendations
        console.log("Loading more: Using stored recommendations for For You tab");
        
        if (window._remainingRecommendations && window._remainingRecommendations.length > 0) {
          // Use remaining recommendations
          moreQuestionsData = {
            questions: window._remainingRecommendations.slice(0, 10),
            hasMore: window._remainingRecommendations.length > 10
          };
          
          // Update remaining recommendations
          window._remainingRecommendations = window._remainingRecommendations.slice(10);
        } else {
          // Fetch more questions and re-run recommendation algorithm
          const allQuestionsData = await getAllQuestions(nextPage, 100);
          
          if (allQuestionsData.questions && allQuestionsData.questions.length > 0) {
            const recommendedQuestions = await getRecommendedQuestions(
              currentUser?.uid,
              allQuestionsData.questions,
              selectedCategory
            );
            
            moreQuestionsData = {
              questions: recommendedQuestions.slice(0, 10),
              hasMore: recommendedQuestions.length > 10
            };
            
            window._remainingRecommendations = recommendedQuestions.slice(10);
          } else {
            moreQuestionsData = { questions: [], hasMore: false };
          }
        }
      }
      else if (activeTab === 'hot') {
        // For Hot tab, use the currently selected hot algorithm from state
        console.log(`Loading more: Using '${currentHotMode}' hot algorithm for Hot tab`);
        moreQuestionsData = await getAllQuestions(nextPage, 10, userId, false, currentHotMode);
      }
      else {
        // Categories tab - no personalization
        console.log("Loading more: Using standard algorithm for Categories tab");
        moreQuestionsData = await getAllQuestions(nextPage, 10);
      }

      setQuestions(prevQuestions => [...prevQuestions, ...moreQuestionsData.questions]);
      setHasMoreQuestions(moreQuestionsData.hasMore);
      setPage(nextPage);
    } catch (error) {
      console.error('Error loading more questions:', error);
    } finally {
      setLoadingMore(false);
    }
  };
  
  // Handle navigation between questions
  const goToNextQuestion = () => {
    // Track if user skipped without voting (if they're logged in)
    if (currentUser && question && !hasVoted) {
      try {
        const userId = currentUser.uid;
        const skippedKey = `skippedQuestions_${userId}`;
        const skippedData = JSON.parse(localStorage.getItem(skippedKey) || '[]');

        // Only add to skipped list if not already there
        if (!skippedData.includes(question.id)) {
          skippedData.push(question.id);
          localStorage.setItem(skippedKey, JSON.stringify(skippedData));
          console.log(`Tracked skipped question: ${question.id}`);
        }
      } catch (err) {
        console.warn('Error tracking skipped question:', err);
      }
    }

    if (currentQuestionIndex < questions.length - 1) {
      console.log(`🚀 ADVANCING: From index ${currentQuestionIndex} to ${currentQuestionIndex + 1}`);
      setCurrentQuestionIndex(currentQuestionIndex + 1);

      // Preload next 3 images
      const nextIndex = currentQuestionIndex + 1;
      const imagesToPreload = questions
        .slice(nextIndex + 1, nextIndex + 4)
        .map(q => q.imageUrl)
        .filter(Boolean);
      imageCache.preloadImages(imagesToPreload);

      // Check if user has already voted on the next question
      const nextQuestion = questions[currentQuestionIndex + 1];
      if (nextQuestion) {
        const voted = checkUserVotedOnQuestion(nextQuestion.id);
        console.log('Going to next question - vote status:', voted);
        setHasVoted(voted);

        // Update and show category indicator for the new question
        // Get category info for the indicator
        const categoryMapping = {
          "1": { name: 'Life', color: '#FF6B00', emoji: '⚛️' },
          "2": { name: 'Food', color: '#E67E22', emoji: '🍔' },
          "3": { name: 'Dating', color: '#FFC107', emoji: '❤️' },
          "4": { name: 'Fashion', color: '#E91E63', emoji: '👗' },
          "5": { name: 'Politics', color: '#2196F3', emoji: '🗳️' },
          "6": { name: 'Sports', color: '#4CAF50', emoji: '⚽' },
          "7": { name: 'Technology', color: '#9C27B0', emoji: '📱' },
          "8": { name: 'Movies', color: '#FF5722', emoji: '🎬' },
          "9": { name: 'Music', color: '#607D8B', emoji: '🎵' },
          "10": { name: 'Out of the Box', color: '#3F51B5', emoji: '💭' }
        };

        const category = nextQuestion.category ? 
          (categoryMapping[nextQuestion.category] || {
            name: 'Category ' + nextQuestion.category,
            color: '#888888',
            emoji: '❓'
          }) : {
            name: 'Uncategorized',
            color: '#888888',
            emoji: '❓'
          };

        // Force re-render of indicator by completely resetting it
        setShowCategoryIndicator(false);
        setCategoryInfo(null);

        // Set a small delay to ensure animation restarts
        setTimeout(() => {
          setCategoryInfo(category);
          setShowCategoryIndicator(true);
          console.log("Showing category indicator for:", category ? category.name : 'No category');
        }, 50);
      }

      // Reset comments state
      setShowComments(false);
      setCommentCount(0); // Reset comment count for the new question

      // Cancel any auto-advance timer
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
        autoAdvanceTimerRef.current = null;
      }
    } else if (hasMoreQuestions) {
      // Load more questions if we've reached the end of the current batch
      loadMoreQuestions();
    }
  };
  
  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      
      // Check if user has already voted on the previous question
      const prevQuestion = questions[currentQuestionIndex - 1];
      if (prevQuestion) {
        const voted = checkUserVotedOnQuestion(prevQuestion.id);
        console.log('Going to previous question - vote status:', voted);
        setHasVoted(voted);

        // Update and show category indicator for the previous question
        // Get category info for the indicator
        const categoryMapping = {
          "1": { name: 'Life', color: '#FF6B00', emoji: '⚛️' },
          "2": { name: 'Food', color: '#E67E22', emoji: '🍔' },
          "3": { name: 'Dating', color: '#FFC107', emoji: '❤️' },
          "4": { name: 'Fashion', color: '#E91E63', emoji: '👗' },
          "5": { name: 'Politics', color: '#2196F3', emoji: '🗳️' },
          "6": { name: 'Sports', color: '#4CAF50', emoji: '⚽' },
          "7": { name: 'Technology', color: '#9C27B0', emoji: '📱' },
          "8": { name: 'Movies', color: '#FF5722', emoji: '🎬' },
          "9": { name: 'Music', color: '#607D8B', emoji: '🎵' },
          "10": { name: 'Out of the Box', color: '#3F51B5', emoji: '💭' }
        };

        const category = prevQuestion.category ? 
          (categoryMapping[prevQuestion.category] || {
            name: 'Category ' + prevQuestion.category,
            color: '#888888',
            emoji: '❓'
          }) : {
            name: 'Uncategorized',
            color: '#888888',
            emoji: '❓'
          };

        // Force re-render of indicator by completely resetting it
        setShowCategoryIndicator(false);
        setCategoryInfo(null);

        // Set a small delay to ensure animation restarts
        setTimeout(() => {
          setCategoryInfo(category);
          setShowCategoryIndicator(true);
          console.log("Showing category indicator for:", category ? category.name : 'No category');
        }, 50);
      }
      
      // Reset comments state
      setShowComments(false);
      setCommentCount(0); // Reset comment count for the new question
      
      // Cancel any auto-advance timer
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
        autoAdvanceTimerRef.current = null;
      }
    }
  };
  
  // Handle touch start event for swipe detection
  const handleTouchStart = (e) => {
    // Check if we're in the comments section
    if (showComments && e.target.closest('.comments-section')) {
      isScrollingRef.current = true;
      return;
    }
    
    isScrollingRef.current = false;
    touchStartX.current = e.touches[0].clientX;
  };
  
  // Handle touch end event for swipe detection
  const handleTouchEnd = (e) => {
    // If we started touch in comments area, ignore swipe
    if (isScrollingRef.current) return;
    
    touchEndX.current = e.changedTouches[0].clientX;
    handleSwipe();
  };
  
  // Handle swipe logic
  const handleSwipe = () => {
    // Need at least 50px swipe distance to register as a swipe
    const minSwipeDistance = 50;
    const swipeDistance = touchEndX.current - touchStartX.current;

    if (Math.abs(swipeDistance) < minSwipeDistance) return;

    // Increment swipe counter
    const newSwipeCount = swipeCounter + 1;
    setSwipeCounter(newSwipeCount);
    
    // If showing an ad, handle swipe to dismiss ad - but only if allowed
    if (showAd) {
      // Only allow dismissing if the countdown has finished
      if (canSkipAd) {
        setShowAd(false);  // Dismiss the ad on swipe
        // Dispatch the ad closed event
        window.dispatchEvent(new CustomEvent('adClosed'));
      }
      return;
    }
    
    // Check if we should show an ad based on configuration
    if (newSwipeCount % adConfig.frequency.swipesToShowAd === 0 && swipeDistance < 0) { // Only on left swipes (going forward)
      // Skip ad display if user is in ad-free period
      if (isAdFree) {
        console.log("Ad skipped: User is in ad-free period");
      } else {
        // Get last ad timestamp
        const lastAdTime = parseInt(localStorage.getItem('lastAdTime') || '0');
        const currentTime = Date.now();
        const timeSinceLastAd = (currentTime - lastAdTime) / 1000; // in seconds

        // Only show if minimum time has elapsed
        if (timeSinceLastAd >= adConfig.frequency.minTimeBetweenAds) {
          // Randomly choose between display and video ads based on configuration
          const isVideoAd = Math.random() < adConfig.frequency.probabilityOfVideoAd;
          setAdType(isVideoAd ? 'video' : 'display');

          // Choose ad format - mostly fullscreen but occasionally banner
          const formatRandom = Math.random();
          if (formatRandom < 0.7) {
            setAdFormat('fullscreen');
          } else if (formatRandom < 0.9) {
            setAdFormat('interstitial');
          } else {
            setAdFormat('banner');
          }

          // Store timestamp of this ad
          localStorage.setItem('lastAdTime', currentTime.toString());

          // Show the ad and handle the rest of the navigation in a separate step
          setShowAd(true);
          return;
        }
      }
    }

    if (swipeDistance > 0) {
      // Swiped right - go to previous
      if (currentQuestionIndex > 0) {
        goToPreviousQuestion();
      }
    } else {
      // Swiped left - go to next
      if (currentQuestionIndex < questions.length - 1 || hasMoreQuestions) {
        // Track skipped question if swiping left without voting
        if (currentUser && question && !hasVoted) {
          try {
            const userId = currentUser.uid;
            const skippedKey = `skippedQuestions_${userId}`;
            const skippedData = JSON.parse(localStorage.getItem(skippedKey) || '[]');

            // Only add to skipped list if not already there
            if (!skippedData.includes(question.id)) {
              skippedData.push(question.id);
              localStorage.setItem(skippedKey, JSON.stringify(skippedData));
              console.log(`Tracked skipped question via swipe: ${question.id}`);
            }
          } catch (err) {
            console.warn('Error tracking skipped question:', err);
          }
        }

        goToNextQuestion();
      }
    }
  };
  
  // Set up auto-advance timer after voting
  const setupAutoAdvance = () => {
    // Clear any existing timer
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
    }
    
    // Set new timer - advance after 7 seconds if not in comments section
    // and if there's a next question to go to
    autoAdvanceTimerRef.current = setTimeout(() => {
      // Check again at execution time if comments are now showing - don't advance if they are
      if (!showComments && currentQuestionIndex < questions.length - 1) {
        // Add a visual feedback before advancing
        if (questionCardRef.current) {
          questionCardRef.current.classList.add('auto-advancing');
          
          // Remove the class after the transition and go to next question
          setTimeout(() => {
            questionCardRef.current.classList.remove('auto-advancing');
            // Do one final check before advancing to ensure comments still aren't showing
            if (!showComments) {
              goToNextQuestion();
            }
          }, 500);
        } else {
          // One final check before advancing
          if (!showComments) {
            goToNextQuestion();
          }
        }
      }
    }, 7000); // 7 seconds
  };
  
  // Clean up timers on unmount
  // Set up ad countdown timer when ad is shown
  useEffect(() => {
    if (showAd) {
      // Reset countdown and disable skipping
      setAdCountdown(15);
      setCanSkipAd(false);

      // Start the countdown timer
      adCountdownTimerRef.current = setInterval(() => {
        setAdCountdown(prev => {
          // When countdown reaches 0, enable skipping and clear the timer
          if (prev <= 1) {
            setCanSkipAd(true);
            clearInterval(adCountdownTimerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      // Clear timer if ad is dismissed
      if (adCountdownTimerRef.current) {
        clearInterval(adCountdownTimerRef.current);
      }
    }

    // Clean up on unmount
    return () => {
      if (adCountdownTimerRef.current) {
        clearInterval(adCountdownTimerRef.current);
      }
    };
  }, [showAd]);

  useEffect(() => {
    return () => {
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
      }
      if (animationTimerRef.current) {
        cancelAnimationFrame(animationTimerRef.current);
      }
      if (adCountdownTimerRef.current) {
        clearInterval(adCountdownTimerRef.current);
      }
    };
  }, []);
  
  // Reset auto-advance when showing/hiding comments
  useEffect(() => {
    if (showComments && autoAdvanceTimerRef.current) {
      // Cancel auto-advance when user opens comments
      clearTimeout(autoAdvanceTimerRef.current);
    }
  }, [showComments]);
  
  // Detect when user is viewing comments section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const viewportHeight = window.innerHeight;
      
      // If user has scrolled more than 50% of the viewport height, they're probably
      // looking at comments, so we should stop auto-advancing
      if (scrollPosition > viewportHeight * 0.5) {
        setShowComments(true);
        // Cancel auto-advance
        if (autoAdvanceTimerRef.current) {
          clearTimeout(autoAdvanceTimerRef.current);
          autoAdvanceTimerRef.current = null;
        }
      } else {
        setShowComments(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleVote = async (option) => {
    if (!question || hasVoted) return;

    // Check if user is logged in
    if (!currentUser) {
      // Open login modal for non-logged-in users
      setLoginModalOpen(true);
      return;
    }

    // For logged out users, we still allow voting but encourage login
    const isAnonymous = !currentUser;

    try {
      console.log("Voting for option", option);

      // Set hasVoted to true IMMEDIATELY to ensure UI updates
      setHasVoted(true);
      setUserChoice(option);
      setAnimatingResults(true);
      setAnimationStep(1); // Start tallying animation

      // Update local state
      const updatedQuestion = { ...question };
      if (option === 'A') {
        updatedQuestion.optionAVotes += 1;
      } else {
        updatedQuestion.optionBVotes += 1;
      }
      updatedQuestion.totalVotes += 1;

      // Update the question in the questions array
      const updatedQuestions = [...questions];
      updatedQuestions[currentQuestionIndex] = updatedQuestion;
      setQuestions(updatedQuestions);

      // Calculate vote percentages for animation
      const totalVotes = updatedQuestion.totalVotes;
      const newOptionAPercentage = totalVotes > 0 ? Math.round((updatedQuestion.optionAVotes / totalVotes) * 100) : 0;
      const newOptionBPercentage = totalVotes > 0 ? Math.round((updatedQuestion.optionBVotes / totalVotes) * 100) : 0;

      // Reset displayed percentages to 0 for the animation
      setDisplayPercentA(0);
      setDisplayPercentB(0);

      // Animate the percentage counters using requestAnimationFrame for smoother animation
      const startTime = performance.now();
      const animationDuration = 1000; // 1 second animation

      // Clear any existing animation timer
      if (animationTimerRef.current) {
        cancelAnimationFrame(animationTimerRef.current);
      }

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / animationDuration, 1);

        // Calculate current animation values using easing
        const easedProgress = easeOutQuad(progress);
        setDisplayPercentA(Math.round(easedProgress * newOptionAPercentage));
        setDisplayPercentB(Math.round(easedProgress * newOptionBPercentage));

        // When animation completes
        if (progress >= 1) {
          setDisplayPercentA(newOptionAPercentage);
          setDisplayPercentB(newOptionBPercentage);
          setAnimationStep(2); // Animation complete
          setAnimatingResults(false);
        } else {
          animationTimerRef.current = requestAnimationFrame(animate);
        }
      };

      // Start the animation
      animationTimerRef.current = requestAnimationFrame(animate);

      // Submit the vote to the server
      await submitVote(question.id, option);
      
      // Track interaction for recommendation algorithm
      if (currentUser) {
        await trackInteraction(currentUser.uid, question.id, 'vote', question.category);
      }

      // Store vote in user-specific or anonymous localStorage
      if (currentUser) {
        // Use user-specific keys to avoid conflicts between accounts
        const userVotedQuestionsKey = `votedQuestions_${currentUser.uid}`;
        const userVoteOptionsKey = `userVoteOptions_${currentUser.uid}`;

        console.log(`Saving vote for user ${currentUser.uid} to keys:`, userVotedQuestionsKey, userVoteOptionsKey);

        // Save voted questions in user-specific storage with timestamp
        const votedQuestions = JSON.parse(localStorage.getItem(userVotedQuestionsKey) || '{}');
        votedQuestions[question.id] = {
          option: option,
          timestamp: Date.now(), // Add timestamp for recency calculations
          categoryId: question.category || null
        };
        localStorage.setItem(userVotedQuestionsKey, JSON.stringify(votedQuestions));

        // Save vote options for styling in user-specific storage
        const userVoteOptions = JSON.parse(localStorage.getItem(userVoteOptionsKey) || '{}');
        userVoteOptions[question.id] = option;
        localStorage.setItem(userVoteOptionsKey, JSON.stringify(userVoteOptions));

        // Add to recently seen categories
        if (question.category) {
          try {
            const recentlySeenKey = `recentlySeenCategories_${currentUser.uid}`;
            const recentlySeen = JSON.parse(localStorage.getItem(recentlySeenKey) || '{}');
            recentlySeen[question.category] = Date.now();
            localStorage.setItem(recentlySeenKey, JSON.stringify(recentlySeen));
          } catch (err) {
            console.warn('Error updating recently seen categories:', err);
          }
        }

        // Remove from skipped if user eventually voted on it
        try {
          const skippedKey = `skippedQuestions_${currentUser.uid}`;
          const skippedData = JSON.parse(localStorage.getItem(skippedKey) || '[]');

          const updatedSkipped = skippedData.filter(id => id !== question.id);
          if (updatedSkipped.length !== skippedData.length) {
            localStorage.setItem(skippedKey, JSON.stringify(updatedSkipped));
          }
        } catch (err) {
          console.warn('Error updating skipped questions:', err);
        }
      } else {
        // Anonymous user storage
        const anonVotedQuestionsKey = 'votedQuestions_anonymous';
        const anonVoteOptionsKey = 'userVoteOptions_anonymous';

        console.log(`Saving vote for anonymous user to keys:`, anonVotedQuestionsKey);

        // Save voted questions in anonymous storage
        const votedQuestions = JSON.parse(localStorage.getItem(anonVotedQuestionsKey) || '{}');
        votedQuestions[question.id] = {
          option: option,
          timestamp: Date.now()
        };
        localStorage.setItem(anonVotedQuestionsKey, JSON.stringify(votedQuestions));

        // Save vote options for styling
        const voteOptions = JSON.parse(localStorage.getItem(anonVoteOptionsKey) || '{}');
        voteOptions[question.id] = option;
        localStorage.setItem(anonVoteOptionsKey, JSON.stringify(voteOptions));
      }

      console.log("Vote successful, updated state:", { hasVoted: true, question: updatedQuestion });

      // Set timer to auto-advance after voting
      setupAutoAdvance();
    } catch (error) {
      console.error('Error submitting vote:', error);
    }
  };
  
  // Easing function for smoother animation
  const easeOutQuad = (t) => t * (2 - t);
  
  // Skip loading screen for authenticated users to prevent black screen
  const isAuthenticatedUser = currentUser && !currentUser.isAnonymous;
  
  if (loading && !isAuthenticatedUser) {
    return (
      <div className="container">
        <div className="full-screen-state loading-state">
          <div className="emoji-loader">
            <span className="emoji-item">👍</span>
            <span className="emoji-item">👎</span>
            <span className="emoji-item">🍅</span>
            <span className="emoji-item">👏</span>
          </div>
          <h2 style={{ marginTop: '2rem' }}>Loading...</h2>
          <p>Let's Settle This</p>
        </div>
      </div>
    );
  }
  
  if (!question || questions.length === 0) {
    console.log('Home component: Rendering no-questions state');
    console.log('Current questions state:', questions);
    console.log('Current question value:', question);
    console.log('Current question index:', currentQuestionIndex);
    console.log('Loading state:', loading);
    console.log('Loading timeout triggered:', loadingTimeout);
    
    return (
      <div className="container">
        <div className="full-screen-state loading-state">
          <div className="emoji-loader">
            <span className="emoji-item">👍</span>
            <span className="emoji-item">👎</span>
            <span className="emoji-item">🍅</span>
            <span className="emoji-item">👏</span>
          </div>
          <h2 style={{ marginTop: '2rem' }}>Loading...</h2>
          <p>Let's Settle This</p>
        </div>
      </div>
    );
  }
  
  // Calculate vote percentages for the current question
  const optionAPercentage = question.totalVotes > 0 
    ? Math.round((question.optionAVotes / question.totalVotes) * 100) 
    : 0;
  const optionBPercentage = question.totalVotes > 0 
    ? Math.round((question.optionBVotes / question.totalVotes) * 100) 
    : 0;
  
  return (
    <>
      <div className="container" style={{ maxWidth: '600px', margin: '0 auto', position: 'relative', overflow: 'hidden' }}>
      {/* Logo positioned inside container to align with content */}
      <AppLogo />
      
      {/* No overlaid ads - now handled as separate slides */}
      
      {/* Login Modal */}
      <LoginModal
        isOpen={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
      />

      {/* Tomato Refill Modal */}
      <TomatoRefillModal
        isOpen={showTomatoRefillModal}
        onClose={() => {
          console.log('TomatoRefillModal onClose called');
          setShowTomatoRefillModal(false);
        }}
      />

      {/* Clap Refill Modal */}
      <ClapRefillModal
        isOpen={showClapRefillModal}
        onClose={() => {
          console.log('ClapRefillModal onClose called');
          setShowClapRefillModal(false);
        }}
      />
      
      {/* Old Tomato Refill Modal - TO BE REMOVED */}
      {false && showTomatoRefillModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 2000,
        }}>
          <div style={{
            width: '90%',
            maxWidth: '320px',
            backgroundColor: 'white',
            borderRadius: '20px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Modal header */}
            <div style={{
              padding: '15px',
              borderBottom: '1px solid #eeeeee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#FF6B00',
              color: 'white'
            }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>
                🍅 Out of Tomatoes
              </h2>
              <button
                onClick={() => setShowTomatoRefillModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0
                }}
              >
                &times;
              </button>
            </div>

            {/* Modal content */}
            <div style={{
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '15px'
            }}>
              <p style={{ margin: 0, textAlign: 'center' }}>
                You've used all your tomatoes for throwing!
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {/* Watch ad option */}
                <button
                  onClick={refillTomatoesByAd}
                  disabled={tomatoRefillsUsedToday >= 2}
                  style={{
                    backgroundColor: tomatoRefillsUsedToday >= 2 ? '#cccccc' : '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '12px',
                    fontSize: '14px',
                    cursor: tomatoRefillsUsedToday >= 2 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <span>Watch an Ad for 10 Free Tomatoes</span>
                  <span style={{ fontSize: '18px' }}>🎬</span>
                </button>

                {tomatoRefillsUsedToday >= 2 && (
                  <p style={{ margin: 0, fontSize: '12px', color: '#666', textAlign: 'center' }}>
                    You've used all your ad refills for today
                  </p>
                )}

                <div style={{ textAlign: 'center', margin: '5px 0', fontSize: '14px' }}>- OR -</div>

                {/* Purchase options */}
                <button
                  onClick={() => purchaseTomatoPack(20)}
                  style={{
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '12px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <span>20 Tomatoes</span>
                  <span>$0.99</span>
                </button>

                <button
                  onClick={() => purchaseTomatoPack(50)}
                  style={{
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '12px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <span>50 Tomatoes</span>
                  <span>$1.99</span>
                </button>

                <button
                  onClick={() => purchaseTomatoPack(120)}
                  style={{
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '12px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    backgroundColor: '#FF6B00',
                    color: 'white',
                    padding: '2px 6px',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    borderBottomLeftRadius: '5px'
                  }}>
                    BEST VALUE
                  </div>
                  <span>120 Tomatoes</span>
                  <span>$3.99</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shop Modal */}
      {showShopModal && currentUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 2000,
        }}>
          <div style={{
            width: '80%',
            maxWidth: '350px',
            height: '70vh',
            backgroundColor: 'white',
            borderRadius: '20px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Modal header */}
            <div style={{
              padding: '20px 15px',
              borderBottom: '1px solid #eeeeee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#FF6B00',
              color: 'white'
            }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: 'white' }}>
                Debatery
              </h2>
              <button
                onClick={() => setShowShopModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0
                }}
              >
                &times;
              </button>
            </div>

            {/* Modal content - placeholder for now */}
            <div style={{
              padding: '20px',
              flex: 1,
              overflowY: 'auto'
            }}>
              <h3 style={{ textAlign: 'center', color: 'black', margin: '0 0 15px 0', fontSize: '18px' }}>
                Items
              </h3>

              {/* Tomato refill timer */}
              <div style={{
                padding: '15px',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                borderRadius: '12px',
                marginBottom: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontSize: '14px', fontWeight: 'bold', color: 'black' }}>Daily Tomato Refill</span>
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    {(() => {
                      // Calculate time until midnight
                      const now = new Date();
                      const midnight = new Date(now);
                      midnight.setHours(24, 0, 0, 0); // Midnight tonight
                      
                      const msUntilReset = midnight - now;
                      const hoursUntilReset = Math.floor(msUntilReset / (1000 * 60 * 60));
                      const minutesUntilReset = Math.floor((msUntilReset % (1000 * 60 * 60)) / (1000 * 60));
                      
                      if (hoursUntilReset > 0) {
                        return `${hoursUntilReset}h ${minutesUntilReset}m remaining`;
                      } else {
                        return `${minutesUntilReset}m remaining`;
                      }
                    })()}
                  </span>
                </div>

                {/* Progress bar */}
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: (() => {
                      // Calculate progress (0 at midnight, 100 at 11:59 PM)
                      const now = new Date();
                      const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                      const msSinceMidnight = now - todayMidnight;
                      const msInDay = 24 * 60 * 60 * 1000;
                      const dayProgress = (msSinceMidnight / msInDay) * 100;
                      return `${dayProgress}%`;
                    })(),
                    backgroundColor: '#FF6B00',
                    borderRadius: '4px'
                  }} />
                </div>

                <div style={{ fontSize: '12px', color: '#666', textAlign: 'center' }}>
                  You get 10 free tomatoes every 24 hours
                </div>

                {/* Quick refill section - always shown */}
                <div style={{
                  marginTop: '10px',
                  padding: '10px',
                  backgroundColor: 'rgba(255, 107, 0, 0.15)',
                  borderRadius: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      fontWeight: 'bold',
                      color: '#333'
                    }}>
                      <span role="img" aria-label="tomato" style={{ fontSize: '16px' }}>🍅</span>
                      <span>Need tomatoes now?</span>
                    </div>
                    <div style={{
                      backgroundColor: '#FF6B00',
                      color: 'white',
                      fontWeight: 'bold',
                      padding: '3px 8px',
                      borderRadius: '12px',
                      fontSize: '12px'
                    }}>
                      {getTomatoCountSync(currentUser?.uid || 'anonymous_user')}
                    </div>
                  </div>

                  <button
                    onClick={refillTomatoesByAd}
                    disabled={tomatoRefillsUsedToday >= 2}
                    style={{
                      backgroundColor: tomatoRefillsUsedToday >= 2 ? '#cccccc' : '#FF6B00',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 16px',
                      fontSize: '14px',
                      cursor: tomatoRefillsUsedToday >= 2 ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold',
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    {tomatoRefillsUsedToday >= 2 ? (
                      <>
                        <span>Ad refills used today</span>
                        <span style={{
                          fontSize: '12px',
                          padding: '2px 6px',
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          borderRadius: '10px'
                        }}>
                          0/2
                        </span>
                      </>
                    ) : (
                      <>
                        <span>Watch Ad for 10 Tomatoes</span>
                        <span style={{
                          fontSize: '12px',
                          padding: '2px 6px',
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          borderRadius: '10px'
                        }}>
                          {2-tomatoRefillsUsedToday}/2
                        </span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Clap refill timer */}
              <div style={{
                padding: '15px',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                borderRadius: '12px',
                marginBottom: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontSize: '14px', fontWeight: 'bold', color: 'black' }}>Daily Clap Refill</span>
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    {(() => {
                      // Calculate time until midnight
                      const now = new Date();
                      const midnight = new Date(now);
                      midnight.setHours(24, 0, 0, 0); // Midnight tonight
                      
                      const msUntilReset = midnight - now;
                      const hoursUntilReset = Math.floor(msUntilReset / (1000 * 60 * 60));
                      const minutesUntilReset = Math.floor((msUntilReset % (1000 * 60 * 60)) / (1000 * 60));
                      
                      if (hoursUntilReset > 0) {
                        return `${hoursUntilReset}h ${minutesUntilReset}m remaining`;
                      } else {
                        return `${minutesUntilReset}m remaining`;
                      }
                    })()}
                  </span>
                </div>

                {/* Progress bar */}
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: (() => {
                      // Calculate progress (0 at midnight, 100 at 11:59 PM)
                      const now = new Date();
                      const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                      const msSinceMidnight = now - todayMidnight;
                      const msInDay = 24 * 60 * 60 * 1000;
                      const dayProgress = (msSinceMidnight / msInDay) * 100;
                      return `${dayProgress}%`;
                    })(),
                    backgroundColor: '#4CAF50',
                    borderRadius: '4px'
                  }} />
                </div>

                <div style={{ fontSize: '12px', color: '#666', textAlign: 'center' }}>
                  You get 10 free claps every 24 hours
                </div>

                {/* Quick refill section for claps */}
                <div style={{
                  marginTop: '10px',
                  padding: '10px',
                  backgroundColor: 'rgba(76, 175, 80, 0.15)',
                  borderRadius: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      fontWeight: 'bold',
                      color: 'black'
                    }}>
                      <span role="img" aria-label="clap" style={{ fontSize: '16px' }}>👏</span>
                      <span>Need claps now?</span>
                    </div>
                    <div style={{
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      fontWeight: 'bold',
                      padding: '3px 8px',
                      borderRadius: '12px',
                      fontSize: '12px'
                    }}>
                      {getClapCountSync(currentUser?.uid || 'anonymous_user')}
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      // For now, we'll just alert that this feature is coming soon
                      alert("Clap refill via ad will be available soon!");
                    }}
                    disabled={clapRefillsUsedToday >= 2}
                    style={{
                      backgroundColor: clapRefillsUsedToday >= 2 ? '#cccccc' : '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 16px',
                      fontSize: '14px',
                      cursor: clapRefillsUsedToday >= 2 ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold',
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                    <span role="img" aria-label="video" style={{ fontSize: '16px' }}>📺</span>
                    Watch an ad for 10 claps ({2 - clapRefillsUsedToday}/2)
                  </button>
                </div>
              </div>

              {/* Extra warning alert when running low on tomatoes */}
              {tomatoCount <= 3 && (
                <div style={{
                  padding: '15px',
                  backgroundColor: 'rgba(255, 107, 0, 0.2)',
                  borderRadius: '12px',
                  marginBottom: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '10px',
                  border: '1px dashed #FF6B00'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    fontWeight: 'bold',
                    color: '#FF6B00'
                  }}>
                    <span role="img" aria-label="warning">⚠️</span>
                    <span>Running low on tomatoes!</span>
                    <span style={{ fontWeight: 'normal' }}>({tomatoCount} left)</span>
                  </div>

                  <div style={{ fontSize: '13px', textAlign: 'center', color: '#FF6B00' }}>
                    Purchase tomato packs below or watch an ad to get more tomatoes.
                  </div>
                </div>
              )}

              {/* Extra warning alert when running low on claps */}
              {clapCount <= 3 && (
                <div style={{
                  padding: '15px',
                  backgroundColor: 'rgba(76, 175, 80, 0.2)',
                  borderRadius: '12px',
                  marginBottom: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '10px',
                  border: '1px dashed #4CAF50'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    fontWeight: 'bold',
                    color: '#4CAF50'
                  }}>
                    <span role="img" aria-label="warning">⚠️</span>
                    <span>Running low on claps!</span>
                    <span style={{ fontWeight: 'normal' }}>({clapCount} left)</span>
                  </div>

                  <div style={{ fontSize: '13px', textAlign: 'center', color: '#4CAF50' }}>
                    Watch an ad to get more claps or wait for the daily refill.
                  </div>
                </div>
              )}

              {/* Ad-free browsing section */}
              <div style={{
                padding: '15px',
                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                borderRadius: '12px',
                marginBottom: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#2196F3', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '18px' }}>🚫</span> Ad-Free Browsing
                </h4>

                <p style={{ fontSize: '14px', color: '#666', margin: '0 0 15px 0' }}>
                  {isAdFree
                    ? "You're currently browsing ad-free!"
                    : adFreeCooldownActive
                      ? "This feature is on cooldown. Try again in a few minutes."
                      : "Watch an ad now to unlock 20 minutes of ad-free browsing."}
                </p>

                <button
                  onClick={watchAdForAdFree}
                  disabled={isAdFree || adFreeCooldownActive}
                  style={{
                    backgroundColor: isAdFree || adFreeCooldownActive ? '#cccccc' : '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    cursor: isAdFree || adFreeCooldownActive ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                >
                  <span role="img" aria-label="video" style={{ fontSize: '16px' }}>📺</span>
                  {isAdFree
                    ? "Ad-free period active"
                    : adFreeCooldownActive
                      ? "On cooldown - wait 20 minutes"
                      : "Watch ad for 20 minutes of ad-free browsing"}
                </button>

                {/* Only show for logged-in users */}
                {currentUser && (
                  <div style={{ marginTop: '15px', borderTop: '1px dashed #2196F3', paddingTop: '15px' }}>
                    <h5 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#2196F3', fontWeight: 'bold' }}>
                      Premium Option
                    </h5>
                    <button
                      onClick={() => {
                        alert("In a real app, this would process a payment to remove ads permanently.\n\nThis is a premium feature that would typically cost around $4.99.");
                      }}
                      style={{
                        backgroundColor: '#673AB7',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '5px'
                      }}
                    >
                      <span role="img" aria-label="star" style={{ fontSize: '16px' }}>⭐</span>
                      Remove Ads Forever ($4.99)
                    </button>
                    <p style={{ fontSize: '12px', color: '#666', margin: '10px 0 0 0', textAlign: 'center' }}>
                      One-time purchase, never see ads again!
                    </p>
                  </div>
                )}
              </div>

              {/* Tomatoes section */}
              <div style={{
                padding: '15px',
                backgroundColor: 'rgba(255, 107, 0, 0.1)',
                borderRadius: '12px',
                marginBottom: '20px'
              }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#FF6B00', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '18px' }}>🍅</span> Tomato Packs
                </h4>
                <p style={{ fontSize: '14px', color: '#666', margin: '0 0 15px 0' }}>
                  Buy tomatoes to throw at comments. You have <strong>{tomatoCount}</strong> tomatoes left.
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <button
                    onClick={() => purchaseTomatoPack(20)}
                    style={{
                      backgroundColor: 'white',
                      color: '#333',
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      padding: '10px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <span>20 Tomatoes</span>
                    <span>$0.99</span>
                  </button>

                  <button
                    onClick={() => purchaseTomatoPack(50)}
                    style={{
                      backgroundColor: 'white',
                      color: '#333',
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      padding: '10px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <span>50 Tomatoes</span>
                    <span>$1.99</span>
                  </button>

                  <button
                    onClick={() => purchaseTomatoPack(120)}
                    style={{
                      backgroundColor: 'white',
                      color: '#333',
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      padding: '10px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      backgroundColor: '#FF6B00',
                      color: 'white',
                      padding: '2px 6px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      borderBottomLeftRadius: '5px'
                    }}>
                      BEST VALUE
                    </div>
                    <span>120 Tomatoes</span>
                    <span>$3.99</span>
                  </button>
                </div>
              </div>

              <div style={{ margin: '20px 0' }}>
                {/* Premium Membership */}
                <div style={{
                  padding: '15px',
                  borderRadius: '10px',
                  backgroundColor: '#f8f9fa',
                  marginBottom: '15px',
                  border: '1px solid #e9ecef'
                }}>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Premium Membership</h3>
                  <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>
                    Get exclusive access to premium features
                  </p>
                  <button style={{
                    backgroundColor: '#FF6B00',
                    color: 'white',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    width: '100%'
                  }}>
                    $9.99 / month
                  </button>
                </div>

                {/* Comment Booster */}
                <div style={{
                  padding: '15px',
                  borderRadius: '10px',
                  backgroundColor: '#f8f9fa',
                  marginBottom: '15px',
                  border: '1px solid #e9ecef'
                }}>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Comment Booster</h3>
                  <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>
                    Boost visibility of your comments for 24 hours
                  </p>
                  <button style={{
                    backgroundColor: '#FF6B00',
                    color: 'white',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    width: '100%'
                  }}>
                    $1.99
                  </button>
                </div>

                {/* Custom Themes */}
                <div style={{
                  padding: '15px',
                  borderRadius: '10px',
                  backgroundColor: '#f8f9fa',
                  marginBottom: '15px',
                  border: '1px solid #e9ecef'
                }}>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Custom Profile Themes</h3>
                  <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>
                    Personalize your profile with exclusive themes
                  </p>
                  <button style={{
                    backgroundColor: '#FF6B00',
                    color: 'white',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    width: '100%'
                  }}>
                    $3.99
                  </button>
                </div>

                {/* Token Packs */}
                <div style={{
                  padding: '15px',
                  borderRadius: '10px',
                  backgroundColor: '#f8f9fa',
                  marginBottom: '15px',
                  border: '1px solid #e9ecef'
                }}>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Token Pack</h3>
                  <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>
                    500 tokens to spend on in-app features
                  </p>
                  <button style={{
                    backgroundColor: '#FF6B00',
                    color: 'white',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    width: '100%'
                  }}>
                    $4.99
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="fixed-header" style={{
        width: "100%",
        maxWidth: "600px",
        margin: "0 auto",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2000
      }}>
        
        {/* User Menu visible on all tabs */}
        <div style={{ position: "absolute", top: "5px", right: "10px", zIndex: 1000 }}>
          <UserMenu onLoginClick={() => setLoginModalOpen(true)} />
        </div>
      </div>

        {/* Category indicator below navigation but on same vertical line as logo */}
        {showCategoryIndicator && categoryInfo && activeTab === 'forYou' && (
          <div
            key={`category-indicator-${activeTab}-${currentQuestionIndex}`}
            style={{
              position: "absolute",
              top: "100px", // Position below navigation
              left: "10px", // Same left position as logo
              zIndex: 1000,
              animation: 'slideInOut 3s forwards',
              backgroundColor: categoryInfo.color,
              color: 'white',
              padding: '5px 8px',
              borderRadius: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              border: '1px solid rgba(255,255,255,0.6)',
              fontSize: '10px',
              fontWeight: '600',
              whiteSpace: 'nowrap'
            }}
          >
            <span style={{ fontSize: '12px', marginRight: '2px' }}>{categoryInfo.emoji}</span>
            {categoryInfo.name}
          </div>
        )}

        {/* Hot items popup on For You page - temporary indicators like categories */}
        {activeTab === 'forYou' && question && (
          <div 
            key={`hot-indicators-${activeTab}-${currentQuestionIndex}`}
            style={{
              position: "absolute",
              top: "100px", // Position below navigation
              right: "10px", // Position on the right side
              zIndex: 1000,
              display: "flex",
              flexDirection: "column",
              gap: "10px"
            }}>

            {/* Trending hot indicator - only appears if trending was used */}
            {question && question._hotAlgorithmUsed === 'trending' && (() => {
              console.log(`Showing trending indicator for question ${currentQuestionIndex}, algorithm: ${question._hotAlgorithmUsed}`);
              return true;
            })() && <div
              style={{
                backgroundColor: "#FF6B00", // Trending color
                color: 'white',
                padding: '5px 8px',
                borderRadius: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                border: '1px solid rgba(255,255,255,0.6)',
                fontSize: '10px',
                fontWeight: '600',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                animation: 'slideInOutRight 3s forwards'
              }}
              onClick={() => {
              // Handle clicking on Trending in the For You page - work for all users
              {
                const hotMode = 'trending';
                try {
                  // Track user interest in this hot algorithm (works for anonymous too)
                  const userKey = currentUser ? currentUser.uid : 'anonymous';
                  const hotTabPrefsKey = `hotTabPreferences_${userKey}`;
                  const hotTabPrefs = JSON.parse(localStorage.getItem(hotTabPrefsKey) || '{}');
                  hotTabPrefs[hotMode] = (hotTabPrefs[hotMode] || 0) + 1;
                  localStorage.setItem(hotTabPrefsKey, JSON.stringify(hotTabPrefs));

                  // Refetch questions with a blend of personalization and this hot algorithm
                  const userId = currentUser ? currentUser.uid : 'anonymous_user';
                  setLoading(true);
                  getAllQuestions(1, 10, userId, true, hotMode).then(data => {
                    // Mark questions as influenced by hot algorithm
                    if (data.questions && data.questions.length > 0) {
                      data.questions.forEach(q => {
                        q._hotAlgorithmUsed = hotMode;
                      });
                    }
                    // ANTI-FLASH: Only reset index if we're changing contexts or don't have questions
                    const shouldResetIndex = questions.length === 0 || 
                      (questions[0] && data.questions[0] && questions[0].id !== data.questions[0].id);
                    
                    setQuestions(data.questions);
                    setHasMoreQuestions(data.hasMore);
                    
                    if (shouldResetIndex) {
                      console.log('🔄 Resetting question index - context changed');
                      setCurrentQuestionIndex(0);
                    } else {
                      console.log('✋ Keeping current question index - same context');
                    }
                    
                    setLoading(false);

                    // Check vote status of first question
                    if (data.questions.length > 0) {
                      const firstQuestionId = data.questions[0].id;
                      const voted = checkUserVotedOnQuestion(firstQuestionId);
                      setHasVoted(voted);
                    }
                  });
                } catch (err) {
                  console.warn('Error updating hot preferences:', err);
                }
              }
            }}>
              <span style={{ fontSize: '12px', marginRight: '2px' }}>🔥</span>
              Trending
              <span 
                style={{ 
                  marginLeft: '4px', 
                  cursor: 'pointer',
                  color: hotTopicInterests['trending'] ? '#FFD700' : 'rgba(255,255,255,0.7)',
                  textShadow: hotTopicInterests['trending'] ? '0 0 3px rgba(255,215,0,0.7)' : 'none'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleHotTopicInterest('trending');
                }}
                title={hotTopicInterests['trending'] ? 'Remove from interests' : 'Add to interests'}
              >
                {hotTopicInterests['trending'] ? '★' : '☆'}
              </span>
            </div>}

            {/* Popular hot indicator - only appears if popular was used */}
            {question && question._hotAlgorithmUsed === 'popular' && (() => {
              console.log(`Showing popular indicator for question ${currentQuestionIndex}, algorithm: ${question._hotAlgorithmUsed}`);
              return true;
            })() && <div
              style={{
                backgroundColor: "#E91E63", // Popular color
                color: 'white',
                padding: '5px 8px',
                borderRadius: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                border: '1px solid rgba(255,255,255,0.6)',
                fontSize: '10px',
                fontWeight: '600',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                animation: 'slideInOutRight 4s forwards'
              }}
              onClick={() => {
              // Similar logic for Popular mode - work for all users
              {
                const hotMode = 'popular';
                try {
                  const userKey = currentUser ? currentUser.uid : 'anonymous';
                  const hotTabPrefsKey = `hotTabPreferences_${userKey}`;
                  const hotTabPrefs = JSON.parse(localStorage.getItem(hotTabPrefsKey) || '{}');
                  hotTabPrefs[hotMode] = (hotTabPrefs[hotMode] || 0) + 1;
                  localStorage.setItem(hotTabPrefsKey, JSON.stringify(hotTabPrefs));

                  const userId = currentUser ? currentUser.uid : 'anonymous_user';
                  setLoading(true);
                  getAllQuestions(1, 10, userId, true, hotMode).then(data => {
                    // Mark questions as influenced by hot algorithm
                    if (data.questions && data.questions.length > 0) {
                      data.questions.forEach(q => {
                        q._hotAlgorithmUsed = hotMode;
                      });
                    }
                    // ANTI-FLASH: Only reset index if we're changing contexts or don't have questions
                    const shouldResetIndex = questions.length === 0 || 
                      (questions[0] && data.questions[0] && questions[0].id !== data.questions[0].id);
                    
                    setQuestions(data.questions);
                    setHasMoreQuestions(data.hasMore);
                    
                    if (shouldResetIndex) {
                      console.log('🔄 Resetting question index - context changed');
                      setCurrentQuestionIndex(0);
                    } else {
                      console.log('✋ Keeping current question index - same context');
                    }
                    
                    setLoading(false);

                    if (data.questions.length > 0) {
                      const firstQuestionId = data.questions[0].id;
                      const voted = checkUserVotedOnQuestion(firstQuestionId);
                      setHasVoted(voted);
                    }
                  });
                } catch (err) {
                  console.warn('Error updating hot preferences:', err);
                }
              }
            }}>
              <span style={{ fontSize: '12px', marginRight: '2px' }}>👑</span>
              Popular
            </div>}

            {/* Rising hot indicator - only appears if rising was used */}
            {question && question._hotAlgorithmUsed === 'rising' && <div
              style={{
                backgroundColor: "#2196F3", // Rising color
                color: 'white',
                padding: '5px 8px',
                borderRadius: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                border: '1px solid rgba(255,255,255,0.6)',
                fontSize: '10px',
                fontWeight: '600',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                animation: 'slideInOutRight 5s forwards'
              }}
              onClick={() => {
              // Similar logic for Rising mode - work for all users
              {
                const hotMode = 'rising';
                try {
                  const userKey = currentUser ? currentUser.uid : 'anonymous';
                  const hotTabPrefsKey = `hotTabPreferences_${userKey}`;
                  const hotTabPrefs = JSON.parse(localStorage.getItem(hotTabPrefsKey) || '{}');
                  hotTabPrefs[hotMode] = (hotTabPrefs[hotMode] || 0) + 1;
                  localStorage.setItem(hotTabPrefsKey, JSON.stringify(hotTabPrefs));

                  const userId = currentUser ? currentUser.uid : 'anonymous_user';
                  setLoading(true);
                  getAllQuestions(1, 10, userId, true, hotMode).then(data => {
                    // Mark questions as influenced by hot algorithm
                    if (data.questions && data.questions.length > 0) {
                      data.questions.forEach(q => {
                        q._hotAlgorithmUsed = hotMode;
                      });
                    }
                    // ANTI-FLASH: Only reset index if we're changing contexts or don't have questions
                    const shouldResetIndex = questions.length === 0 || 
                      (questions[0] && data.questions[0] && questions[0].id !== data.questions[0].id);
                    
                    setQuestions(data.questions);
                    setHasMoreQuestions(data.hasMore);
                    
                    if (shouldResetIndex) {
                      console.log('🔄 Resetting question index - context changed');
                      setCurrentQuestionIndex(0);
                    } else {
                      console.log('✋ Keeping current question index - same context');
                    }
                    
                    setLoading(false);

                    if (data.questions.length > 0) {
                      const firstQuestionId = data.questions[0].id;
                      const voted = checkUserVotedOnQuestion(firstQuestionId);
                      setHasVoted(voted);
                    }
                  });
                } catch (err) {
                  console.warn('Error updating hot preferences:', err);
                }
              }
            }}>
              <span style={{ fontSize: '12px', marginRight: '2px' }}>📈</span>
              Rising
            </div>}

            {/* New hot indicator - only appears if new was used */}
            {question && question._hotAlgorithmUsed === 'new' && <div
              style={{
                backgroundColor: "#4CAF50", // New color
                color: 'white',
                padding: '5px 8px',
                borderRadius: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                border: '1px solid rgba(255,255,255,0.6)',
                fontSize: '10px',
                fontWeight: '600',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                animation: 'slideInOutRight 4.5s forwards'
              }}
              onClick={() => {
              // Similar logic for New mode - work for all users
              {
                const hotMode = 'new';
                try {
                  const userKey = currentUser ? currentUser.uid : 'anonymous';
                  const hotTabPrefsKey = `hotTabPreferences_${userKey}`;
                  const hotTabPrefs = JSON.parse(localStorage.getItem(hotTabPrefsKey) || '{}');
                  hotTabPrefs[hotMode] = (hotTabPrefs[hotMode] || 0) + 1;
                  localStorage.setItem(hotTabPrefsKey, JSON.stringify(hotTabPrefs));

                  const userId = currentUser ? currentUser.uid : 'anonymous_user';
                  setLoading(true);
                  getAllQuestions(1, 10, userId, true, hotMode).then(data => {
                    // Mark questions as influenced by hot algorithm
                    if (data.questions && data.questions.length > 0) {
                      data.questions.forEach(q => {
                        q._hotAlgorithmUsed = hotMode;
                      });
                    }
                    // ANTI-FLASH: Only reset index if we're changing contexts or don't have questions
                    const shouldResetIndex = questions.length === 0 || 
                      (questions[0] && data.questions[0] && questions[0].id !== data.questions[0].id);
                    
                    setQuestions(data.questions);
                    setHasMoreQuestions(data.hasMore);
                    
                    if (shouldResetIndex) {
                      console.log('🔄 Resetting question index - context changed');
                      setCurrentQuestionIndex(0);
                    } else {
                      console.log('✋ Keeping current question index - same context');
                    }
                    
                    setLoading(false);

                    if (data.questions.length > 0) {
                      const firstQuestionId = data.questions[0].id;
                      const voted = checkUserVotedOnQuestion(firstQuestionId);
                      setHasVoted(voted);
                    }
                  });
                } catch (err) {
                  console.warn('Error updating hot preferences:', err);
                }
              }
            }}>
              <span style={{ fontSize: '12px', marginRight: '2px' }}>✨</span>
              New
            </div>}

          </div>
        )}
        
        {/* Navigation tabs (visible on all tabs) */}
        <div style={{
          position: "absolute",
          top: "55px",
          left: "0",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          zIndex: 1000
        }}>
        <div style={{ display: "flex", width: "400px", justifyContent: "space-between" }}>
          <div
            style={{
              position: "relative",
              cursor: "pointer",
              padding: "0 5px",
              textShadow: "0 1px 2px rgba(0,0,0,0.8)"
            }}
            data-tab="categories"
            onClick={() => {
              console.log("Switching to Categories tab");
              setActiveTab('categories');
            }}
          >
            <span style={{
              fontSize: "16px",
              color: activeTab === 'categories' ? "#FF6B00" : "white",
              fontWeight: activeTab === 'categories' ? "700" : "500"
            }}>
              Categories
            </span>
            {activeTab === 'categories' && (
              <div style={{ position: "absolute", bottom: "-5px", left: "0", width: "100%", height: "3px", backgroundColor: "#FF6B00", borderRadius: "2px" }}></div>
            )}
          </div>
          <div
            style={{
              position: "relative",
              cursor: "pointer",
              padding: "0 5px",
              textShadow: "0 1px 2px rgba(0,0,0,0.8)"
            }}
            onClick={() => {
              console.log("Switching to For You tab");
              setActiveTab('forYou');
              
              // Clear any ongoing animations before switching
              setShowCategoryIndicator(false);
              setCategoryInfo(null);
              
              // Mark existing questions with hot algorithm indicators when switching to For You
              // Use setTimeout to avoid animation conflicts
              setTimeout(() => {
                setQuestions(prevQuestions => {
                  return prevQuestions.map((question, index) => {
                    const hotAlgorithm = calculateHotAlgorithm(question);
                    console.log(`Tab switch - Question ${index}: assigned hot algorithm: ${hotAlgorithm} based on engagement`);
                    return {
                      ...question,
                      _hotAlgorithmUsed: hotAlgorithm
                    };
                  });
                });
              }, 100);
            }}
          >
            <span style={{
              fontSize: "16px",
              color: activeTab === 'forYou' ? "#FF6B00" : "white",
              fontWeight: activeTab === 'forYou' ? "700" : "500"
            }}>
              For You
            </span>
            {activeTab === 'forYou' && (
              <div style={{ position: "absolute", bottom: "-5px", left: "0", width: "100%", height: "3px", backgroundColor: "#FF6B00", borderRadius: "2px" }}></div>
            )}
          </div>
          <div
            style={{
              position: "relative",
              cursor: "pointer",
              padding: "0 5px",
              textShadow: "0 1px 2px rgba(0,0,0,0.8)"
            }}
            onClick={() => {
              console.log("Switching to Following tab");
              window.location.href = '/following';
            }}
          >
            <span style={{
              fontSize: "16px",
              color: "white",
              fontWeight: "500"
            }}>
              Following
            </span>
          </div>
          <div
            style={{
              position: "relative",
              cursor: "pointer",
              padding: "0 5px",
              textShadow: "0 1px 2px rgba(0,0,0,0.8)"
            }}
            onClick={() => {
              console.log("Switching to Hot tab");
              setActiveTab('hot');
            }}
          >
            <span style={{
              fontSize: "16px",
              color: activeTab === 'hot' ? "#FF6B00" : "white",
              fontWeight: activeTab === 'hot' ? "700" : "500"
            }}>
              Hot 🔥
            </span>
            {activeTab === 'hot' && (
              <div style={{ position: "absolute", bottom: "-5px", left: "0", width: "100%", height: "3px", backgroundColor: "#FF6B00", borderRadius: "2px" }}></div>
            )}
          </div>
        </div>
      </div>

      <>
      <style>
        {`
          @keyframes slideInOut {
            0% { opacity: 0; transform: translateX(-50px); }
            15% { opacity: 1; transform: translateX(0); }
            85% { opacity: 1; transform: translateX(0); }
            100% { opacity: 0; transform: translateX(-50px); }
          }

          @keyframes slideInRight {
            0% { opacity: 0; transform: translateX(50px); }
            100% { opacity: 1; transform: translateX(0); }
          }

          @keyframes slideInOutRight {
            0% { opacity: 0; transform: translateX(50px); }
            15% { opacity: 1; transform: translateX(0); }
            85% { opacity: 1; transform: translateX(0); }
            100% { opacity: 0; transform: translateX(50px); }
          }
        `}
      </style>
      </>

      {/* Display Categories or For You content based on active tab */}
      {activeTab === 'categories' ? (
        <Categories onCategoryClick={(categoryId, questions) => {
          // The event listener above will handle this
        }} />
      ) : activeTab === 'hot' ? (
        <Categories hotMode={true} onCategoryClick={(categoryId, questions) => {
          // We could handle hot mode categories here if needed
        }} />
      ) : (
        <div style={{ width: '100%' }}>
          {/* Placing in the context of the section elements */}

          {/* Selected category indicator */}
          {selectedCategory && (
            <div style={{
              position: 'relative',
              backgroundColor: 'rgba(0,0,0,0.7)',
              color: 'white',
              padding: '10px 15px',
              textAlign: 'center',
              marginBottom: '10px',
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span>
                Viewing questions in category: <strong>{selectedCategory.name}</strong>
              </span>
              <button
                onClick={() => {
                  setSelectedCategory(null);
                  // Reset to regular questions with personalization
                  setLoading(true);

                  // Use personalization for "For You" tab when user is logged in
                  const personalized = activeTab === 'forYou' && currentUser !== null;
                  const userId = currentUser ? currentUser.uid : null;

                  getAllQuestions(1, 10, userId, personalized).then(data => {
                    // ANTI-FLASH: Only reset index if we're changing contexts or don't have questions
                    const shouldResetIndex = questions.length === 0 || 
                      (questions[0] && data.questions[0] && questions[0].id !== data.questions[0].id);
                    
                    setQuestions(data.questions);
                    setHasMoreQuestions(data.hasMore);
                    
                    if (shouldResetIndex) {
                      console.log('🔄 Resetting question index - context changed');
                      setCurrentQuestionIndex(0);
                    } else {
                      console.log('✋ Keeping current question index - same context');
                    }
                    
                    setLoading(false);
                    if (data.questions.length > 0) {
                      const firstQuestionId = data.questions[0].id;
                      const voted = checkUserVotedOnQuestion(firstQuestionId);
                      setHasVoted(voted);
                    }
                  });
                }}
                style={{
                  backgroundColor: '#FF6B00',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '5px 10px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                View All Questions
              </button>
            </div>
          )}

          {/* Ad section: Full-height ad shown as its own slide */}
          {showAd && (
            <section 
              className="ad-slide"
              style={{
                height: '100vh',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                marginBottom: '20px',
                boxSizing: 'border-box'
              }}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <AdContainer
                adType={adType}
                format={adFormat}
                onClose={() => setShowAd(false)}
                duration={adType === 'video' ? 15 : 0} // 15-second countdown for video ads
                testMode={true} // Use test ads during development
              />

              {/* Skip button appears after countdown */}
              {canSkipAd && (
                <div style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  zIndex: 10
                }}>
                  <button
                    onClick={() => setShowAd(false)}
                    style={{
                      backgroundColor: '#FF6B00',
                      color: 'white',
                      border: 'none',
                      borderRadius: '20px',
                      padding: '8px 16px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                    }}
                  >
                    Skip Ad
                  </button>
                </div>
              )}
              <div style={{
                position: 'absolute',
                bottom: '20px',
                textAlign: 'center',
                width: '100%',
                color: 'white',
                fontSize: '14px',
                padding: '10px',
                backgroundColor: 'rgba(0,0,0,0.7)',
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                maxWidth: '300px',
                margin: '0 auto'
              }}>
                {canSkipAd ? (
                  <span>Swipe to continue</span>
                ) : (
                  <>
                    <span>Ad: {adCountdown}s remaining</span>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                  </>
                )}
              </div>
            </section>
          )}

          {/* First section: Full-height image with voting */}
          {!showAd && (
            <section
              className="question-card"
              ref={questionCardRef}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              style={{
                width: '100%',
                maxWidth: '600px',
                margin: '0 auto'
              }}
            >
        
              <div className="question-image-container">
                <QuestionMedia
                  src={question.imageUrl}
                  alt={question.questionText}
                  className="question-image"
                  preloadNext={questions[currentQuestionIndex + 1]?.imageUrl}
                  mediaType={question.mediaType || 'image'}
                  thumbnailUrl={question.thumbnailUrl}
                />
                <div className="overlay"></div>
                
                {/* Invisible navigation areas */}
                <div
                  onClick={goToPreviousQuestion}
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '30%',
                    height: '100%',
                    zIndex: 10,
                    cursor: 'pointer',
                    // backgroundColor: 'rgba(255,0,0,0.1)', // Uncomment to see the area
                  }}
                  title="Previous question"
                />
                <div
                  onClick={goToNextQuestion}
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    width: '30%',
                    height: '100%',
                    zIndex: 10,
                    cursor: 'pointer',
                    // backgroundColor: 'rgba(0,255,0,0.1)', // Uncomment to see the area
                  }}
                  title="Next question"
                />
              </div>
              
              <div className="question-content">
                <h2 className="question-text">{question.questionText}</h2>
                
                {/* For standard questions, either show vote buttons or results */}
                {question.questionType !== 'leaderboard' && (
                  <div>
                    {/* This section will be replaced by the conditional rendering below */}
                  </div>
                )}

                {/* No buttons for leaderboard questions */}
                
                {/* For leaderboard questions, special handling */}
                {question.questionType === 'leaderboard' ? (
                  <div className="leaderboard-results">
                    <h3 style={{
                      textAlign: 'center',
                      marginBottom: '15px',
                      color: 'white',
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                    }}>
                      Top Answers
                    </h3>

                    {/* Always show the leaderboard for leaderboard questions */}
                    <CommentLeaderboard
                      questionId={question.id}
                      styles={{
                        backgroundColor: question.leaderboardStyles?.backgroundColor || 'white',
                        color: question.leaderboardStyles?.textColor || 'black',
                        borderColor: question.leaderboardStyles?.borderColor || '#e0e0e0',
                        borderRadius: question.leaderboardStyles?.borderRadius || '50px',
                        boxShadow: question.leaderboardStyles?.boxShadow || '0 4px 15px rgba(0,0,0,0.1)',
                        padding: question.leaderboardStyles?.padding || '12px 24px',
                        fontSize: question.leaderboardStyles?.fontSize || '16px',
                        fontWeight: question.leaderboardStyles?.fontWeight || '500',
                        // Hover styles
                        hoverBackgroundColor: question.leaderboardStyles?.hoverBackgroundColor || '#f5f5f5',
                        activeBackgroundColor: question.leaderboardStyles?.activeBackgroundColor || '#e0e0e0'
                      }}
                      showTomatoCount={question.leaderboardOptions?.showTomatoCount !== false}
                      showClapCount={question.leaderboardOptions?.showClapCount === true}
                      limit={question.leaderboardOptions?.limit || 3}
                      sortBy={question.leaderboardOptions?.sortBy || 'tomatoes'}
                      onCommentClick={(comment) => {
                        // Scroll to the comment section when a leaderboard item is clicked
                        document.querySelector('.comments-section').scrollIntoView({
                          behavior: 'smooth'
                        });
                      }}
                    />

                    {/* Prompt below the leaderboard */}
                    <div style={{
                      backgroundColor: 'rgba(0,0,0,0.2)',
                      color: 'white',
                      padding: '10px 15px',
                      borderRadius: '8px',
                      marginTop: '15px',
                      textAlign: 'center',
                      fontSize: '14px'
                    }}>
                      Add your own answer in the takes below!
                    </div>
                  </div>
                ) : (
                  /* Always show the vote buttons, even if the user has voted */
                  <div className="vote-options">
                    {/* If the user is logged in and has voted, show vote results */}
                    {(hasVoted && currentUser) ? (
                      // Standard poll results
                      <div className={`vote-results ${animatingResults ? 'animating' : ''}`}>
                        <div className="results-display">
                          <div className="results-bar-container">
                            <div 
                              className={`result-bar option-a ${userChoice === 'A' ? 'user-choice' : ''} ${optionAPercentage >= optionBPercentage ? 'winner' : ''}`}
                              style={{
                                width: `${animatingResults ? displayPercentA : optionAPercentage}%`,
                                backgroundColor: question.styles?.optionABackgroundColor || 'white',
                                minWidth: question.totalVotes > 0 ? '10%' : '50%'
                              }}
                            >
                              <div 
                                className="result-percentage"
                                style={{ color: question.styles?.optionATextColor || 'black' }}
                              >
                                {animatingResults ? displayPercentA : optionAPercentage}%
                              </div>
                            </div>
                            
                            <div 
                              className={`result-bar option-b ${userChoice === 'B' ? 'user-choice' : ''} ${optionBPercentage >= optionAPercentage ? 'winner' : ''}`}
                              style={{
                                width: `${animatingResults ? displayPercentB : optionBPercentage}%`,
                                backgroundColor: question.styles?.optionBBackgroundColor || 'black',
                                minWidth: question.totalVotes > 0 ? '10%' : '50%'
                              }}
                            >
                              <div 
                                className="result-percentage"
                                style={{ color: question.styles?.optionBTextColor || 'white' }}
                              >
                                {animatingResults ? displayPercentB : optionBPercentage}%
                              </div>
                            </div>
                          </div>
                          
                          <div className="results-labels">
                            <div 
                              className="result-label option-a-label"
                              style={{ color: question.styles?.optionATextColor || 'black' }}
                            >
                              {question.optionA}
                            </div>
                            <div 
                              className="result-label option-b-label"
                              style={{ color: question.styles?.optionBTextColor || 'white' }}
                            >
                              {question.optionB}
                            </div>
                          </div>
                          
                        </div>
                      </div>
                    ) : (
                      // For anonymous users or when nobody has voted, always show the vote buttons
                      <>
                        <button
                          className="vote-button"
                          style={{
                            backgroundColor: question.styles?.optionABackgroundColor || 'white',
                            color: question.styles?.optionATextColor || 'black',
                            border: question.styles?.optionABackgroundColor === 'white' ? 'none' : `2px solid ${question.styles?.optionATextColor || 'black'}`
                          }}
                          onClick={() => handleVote('A')}
                        >
                          {question.optionA}
                        </button>
                        <button
                          className="vote-button"
                          style={{
                            backgroundColor: question.styles?.optionBBackgroundColor || 'black',
                            color: question.styles?.optionBTextColor || 'white',
                            border: question.styles?.optionBBackgroundColor === 'black' ? '2px solid white' : `2px solid ${question.styles?.optionBTextColor || 'white'}`
                          }}
                          onClick={() => handleVote('B')}
                        >
                          {question.optionB}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Second section: Takes below */}
          <section className="comments-section" style={{
            width: '100%',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0 10px',
              backgroundColor: '#f9f9fb'
            }}>
              <h3 className="comments-header">
                Takes
              </h3>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '15px'
              }}>
                {/* User's available tomato counter */}
                <TomatoCounter />

                {/* User's available clap counter */}
                <ClapCounter />

                {/* Shop button - only for logged-in users */}
                {currentUser && (
                  <a href="#" onClick={(e) => {
                    e.preventDefault();
                    // Open the shop modal
                    setShowShopModal(true);
                  }}>
                    <img
                      src="/shop button.png"
                      alt="Shop"
                      style={{
                        height: '30px',
                        width: 'auto',
                        cursor: 'pointer'
                      }}
                    />
                  </a>
                )}
              </div>
            </div>
            <div className="comments-scroll-area">
              <CommentSection
                key={question.id} /* Key ensures a new instance when question changes */
                questionId={question.id}
                onCommentCountChange={setCommentCount}
                isLeaderboard={question.questionType === 'leaderboard'} /* Direct prop to control behavior */
              />
            </div>
          </section>
        </div>
      )}
    </div>
    </>
  );
}

export default Home;